import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { api } from '../api.js';
import { Save, Eye, FileText, Sparkles, ChevronDown, ChevronUp, History, RotateCcw, Check, UploadCloud, GripVertical, PlusCircle, Trash2 } from 'lucide-react';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';

const AIC_PAPER_SIZES_MM = {
  A4: { width: 210, height: 297 },
  LETTER: { width: 215.9, height: 279.4 },
};

function cssLengthToMm(value) {
  const match = /^(-?\d+(?:\.\d+)?)\s*(mm|cm|in|pt|px|q)?$/i.exec(String(value || '').trim());
  if (!match) return null;
  const num = Number(match[1]);
  switch ((match[2] || 'px').toLowerCase()) {
    case 'mm': return num;
    case 'cm': return num * 10;
    case 'in': return num * 25.4;
    case 'pt': return (num * 25.4) / 72;
    case 'q': return num * 0.25;
    default: return (num * 25.4) / 96;
  }
}

// Mirror of resolvePaperFromHtml in packages/libs/src/chromium/index.js.
// The preview must resolve the paper the same way the PDF renderer does
// (the template's own @page rule wins over the configured format), otherwise
// preview and render paginate with different page boxes.
function resolvePaperFromHtml(html) {
  const source = String(html || '');
  const rule = /@page[^{]*\{([^}]*)\}/i.exec(source);
  if (!rule) return null;

  const size = /size:\s*([^;}'"]+)/i.exec(rule[1]);
  if (!size) return null;

  const parts = String(size[1]).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;

  const named = { letter: 'LETTER', a4: 'A4' }[parts[0].toLowerCase()];
  if (named) return named;

  if (parts.length >= 2) {
    let width = cssLengthToMm(parts[0]);
    let height = cssLengthToMm(parts[1]);
    if (width !== null && height !== null) {
      if (width > height) [width, height] = [height, width];
      for (const [paper, mm] of Object.entries(AIC_PAPER_SIZES_MM)) {
        if (Math.abs(width - mm.width) <= 2 && Math.abs(height - mm.height) <= 2) return paper;
      }
    }
  }

  return null;
}

const AUTO_PREVIEW_STORAGE_KEY = 'aic_template_editor_auto_preview';
const AI_PROMPT_MAX_CHARS = 4000;
const VISUAL_META_PREFIX = '<!--AIC_VISUAL_BUILDER:';
const PAGE_BREAK_PRINT_CSS_SNIPPET = `<style>
@media print {
  .aic-page-break {
    break-before: page;
    page-break-before: always;
  }

  .aic-keep-together {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>`;
const PAGE_BREAK_MARKER_SNIPPET = '<div class="aic-page-break"></div>';
const PAGE_HEADER_SNIPPET = `<header class="aic-page-header">
  <div style="font-weight:700;font-size:12px;">Encabezado del documento</div>
  <div style="font-size:11px;opacity:.8;">Se repite en cada página</div>
</header>`;
const PAGE_CONTENT_SNIPPET = `<main class="aic-page-content">
  <!-- contenido del documento -->
</main>`;
const KEEP_TOGETHER_SNIPPET = `<section class="aic-keep-together">
  <!-- contenido que no debe romperse entre páginas -->
</section>`;
const PAGINATED_TABLE_SNIPPET = `<table class="aic-page-table">
  <thead>
    <tr>
      <th>Columna</th>
      <th>Valor</th>
    </tr>
  </thead>
  <tbody>
    {{#each rows}}
    <tr>
      <td>{{this.label}}</td>
      <td>{{this.value}}</td>
    </tr>
    {{/each}}
  </tbody>
</table>`;

const VISUAL_BLOCK_TYPES = [
  { type: 'flexContainer', labelKey: 'templateEditor.visualBlock.flexContainer', labelFallback: 'Flex Container' },
  { type: 'heading', labelKey: 'templateEditor.visualBlock.heading', labelFallback: 'Heading' },
  { type: 'text', labelKey: 'templateEditor.visualBlock.text', labelFallback: 'Paragraph' },
  { type: 'textSingleLine', labelKey: 'templateEditor.visualBlock.textSingleLine', labelFallback: 'Text (1 line)' },
  { type: 'conditional', labelKey: 'templateEditor.visualBlock.conditional', labelFallback: 'Conditional Block' },
  { type: 'button', labelKey: 'templateEditor.visualBlock.button', labelFallback: 'Button' },
  { type: 'image', labelKey: 'templateEditor.visualBlock.image', labelFallback: 'Image' },
  { type: 'columns2', labelKey: 'templateEditor.visualBlock.columns2', labelFallback: '2 Columns' },
  { type: 'list', labelKey: 'templateEditor.visualBlock.list', labelFallback: 'List' },
  { type: 'table', labelKey: 'templateEditor.visualBlock.table', labelFallback: 'Table' },
  { type: 'divider', labelKey: 'templateEditor.visualBlock.divider', labelFallback: 'Divider' },
  { type: 'spacer', labelKey: 'templateEditor.visualBlock.spacer', labelFallback: 'Spacer' },
  { type: 'raw', labelKey: 'templateEditor.visualBlock.raw', labelFallback: 'Raw HTML' },
];

function toPrettyJson(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

function parseJsonSafe(value, fallback = {}) {
  try {
    if (typeof value === 'string') return JSON.parse(value || '{}');
    if (value && typeof value === 'object') return value;
    return fallback;
  } catch {
    return fallback;
  }
}

function wrapPreviewDoc(html, options = {}) {
  const paperSize = resolvePaperFromHtml(html)
    || (String(options.paperSize || 'A4').toUpperCase() === 'LETTER' ? 'LETTER' : 'A4');
  const content = html || '';
  const previewCss = `
  :root{
    --aic-preview-bg:#0f1423;
    --aic-sheet-bg:#ffffff;
    --aic-sheet-shadow:0 8px 22px rgba(0,0,0,.28);
    --aic-sheet-width:210mm;
    --aic-sheet-height:297mm;
  }

  html[data-aic-paper='LETTER']{
    --aic-sheet-width:8.5in;
    --aic-sheet-height:11in;
  }

  body{
    margin:0;
    padding:16px;
    background:var(--aic-preview-bg);
    box-sizing:border-box;
  }

  .aic-preview-pages{
    width:max-content;
    min-width:100%;
    margin:0 auto;
    display:flex;
    flex-direction:column;
    gap:18px;
    align-items:center;
  }

  /* Same page box as .aic-print-page in packages/libs/src/chromium/index.js
     so preview pagination matches the PDF render. */
  .aic-preview-page{
    width:var(--aic-sheet-width);
    height:var(--aic-sheet-height);
    background:var(--aic-sheet-bg);
    box-shadow:var(--aic-sheet-shadow);
    overflow:hidden;
    display:flex;
    flex-direction:column;
    position:relative;
    box-sizing:border-box;
    padding:10mm 12mm;
  }

  .aic-preview-page-number{
    position:absolute;
    right:14px;
    bottom:10px;
    font-size:11px;
    color:#6b7280;
    background:rgba(255,255,255,.9);
    border:1px solid #e5e7eb;
    border-radius:999px;
    padding:2px 8px;
    line-height:1.2;
    pointer-events:none;
  }

  .aic-preview-page-header{
    margin-bottom:4mm;
  }

  .aic-preview-page-content{
    box-sizing:border-box;
    flex:1;
    overflow:hidden;
    display:block;
  }

  .aic-flow-root{
    width:100%;
    max-width:100%;
    margin:0 auto;
    min-width:0;
  }

  .aic-page-table thead{
    display:table-header-group;
  }

  .aic-page-table tfoot{
    display:table-footer-group;
  }

  .aic-page-table tbody tr{
    break-inside:avoid;
    page-break-inside:avoid;
  }

  .aic-keep-together{
    break-inside:avoid;
    page-break-inside:avoid;
  }

  .aic-page-break{
    break-before:page;
    page-break-before:always;
  }

  @media print {
    @page { size: A4; margin: 10mm; }
    html[data-aic-paper='LETTER'] @page { size: letter; margin: 10mm; }
    body { background:#fff; padding:0; }
    .aic-preview-pages { gap:0; }
    .aic-preview-page { box-shadow:none; width:auto; min-height:auto; }
    .aic-preview-page-number { color:#111827; background:transparent; border:none; }
  }
  `;

  const paginationScript = `<script>
    (function(){
      function isIgnorableNode(node){
        return node && node.nodeType === 3 && !String(node.textContent || '').trim();
      }

      function normalizeNodes(nodes){
        return nodes.filter((node) => !isIgnorableNode(node));
      }

      function isElement(node){
        return node && node.nodeType === 1;
      }

      function forcePageBreak(node){
        return isElement(node) && node.classList.contains('aic-page-break');
      }

      function isKeepTogether(node){
        return isElement(node) && node.classList.contains('aic-keep-together');
      }

      function isPaginatedTable(node){
        return isElement(node) && node.tagName === 'TABLE' && node.classList.contains('aic-page-table');
      }

      function cloneNodeWithChildren(node, children){
        if (!isElement(node) || !children || children.length === 0) return null;
        const clone = node.cloneNode(false);
        for (const child of children) {
          clone.appendChild(child.cloneNode(true));
        }
        return clone;
      }

      function expandPaginatedContainerNode(node){
        if (!isElement(node) || isPaginatedTable(node)) return [node];

        const children = normalizeNodes(Array.from(node.childNodes || []));
        const tableIndex = children.findIndex((child) => isPaginatedTable(child));
        if (tableIndex === -1) return [node];

        const parts = [];
        const before = cloneNodeWithChildren(node, children.slice(0, tableIndex));
        const after = cloneNodeWithChildren(node, children.slice(tableIndex + 1));

        if (before) parts.push(before);
        parts.push(children[tableIndex]);
        if (after) parts.push(after);
        return parts;
      }

      function expandPaginatedNodes(nodes){
        const expanded = [];
        for (const node of nodes) {
          expanded.push(...expandPaginatedContainerNode(node));
        }
        return expanded;
      }

      function pageHasContent(page){
        return normalizeNodes(Array.from(page.target.childNodes)).length > 0;
      }

      function createPaginatedTableShell(table){
        const shell = table.cloneNode(false);
        for (const child of Array.from(table.children || [])) {
          if (!isElement(child)) continue;
          const tag = String(child.tagName || '').toUpperCase();
          if (tag === 'CAPTION' || tag === 'COLGROUP' || tag === 'THEAD') {
            shell.appendChild(child.cloneNode(true));
          }
        }
        const body = document.createElement('tbody');
        shell.appendChild(body);
        return { shell, body };
      }

      function extractPaginatedTableRows(table){
        const rows = [];
        const tbodies = Array.from(table.tBodies || []);

        if (tbodies.length > 0) {
          for (const tbody of tbodies) {
            for (const row of Array.from(tbody.rows || [])) {
              rows.push(row);
            }
          }
          return rows;
        }

        for (const child of Array.from(table.children || [])) {
          if (isElement(child) && child.tagName === 'TR') {
            rows.push(child);
          }
        }

        return rows;
      }

      function paginateTableNode(table, wrapper, headerTemplate, rootMeta, current){
        const rows = extractPaginatedTableRows(table);
        if (rows.length === 0) {
          current.target.appendChild(table);
          return current;
        }

        function openTableOnPage(page){
          const state = createPaginatedTableShell(table);
          page.target.appendChild(state.shell);
          return state;
        }

        let pageHadOtherContent = pageHasContent(current);
        let tableState = openTableOnPage(current);

        for (const sourceRow of rows) {
          const row = sourceRow.cloneNode(true);
          tableState.body.appendChild(row);

          if (current.content.scrollHeight <= current.content.clientHeight + 1) {
            continue;
          }

          tableState.body.removeChild(row);
          const hasRowsOnPage = tableState.body.childElementCount > 0;
          if (!hasRowsOnPage) {
            current.target.removeChild(tableState.shell);
          }

          if (!hasRowsOnPage && !pageHadOtherContent) {
            tableState = openTableOnPage(current);
            tableState.body.appendChild(row);
            continue;
          }

          current = createPageWithRoot(wrapper, headerTemplate, rootMeta);
          pageHadOtherContent = false;
          tableState = openTableOnPage(current);
          tableState.body.appendChild(row);
        }

        const tfoot = Array.from(table.children || []).find((child) => isElement(child) && String(child.tagName || '').toUpperCase() === 'TFOOT');
        if (tfoot) {
          const footerClone = tfoot.cloneNode(true);
          tableState.shell.appendChild(footerClone);
          if (current.content.scrollHeight > current.content.clientHeight + 1 && tableState.body.childElementCount > 0) {
            tableState.shell.removeChild(footerClone);
            current = createPageWithRoot(wrapper, headerTemplate, rootMeta);
            tableState = openTableOnPage(current);
            tableState.shell.appendChild(tfoot.cloneNode(true));
          }
        }

        return current;
      }

      function createPage(wrapper, headerTemplate){
        const page = document.createElement('section');
        page.className = 'aic-preview-page';

        if (headerTemplate) {
          const header = document.createElement('div');
          header.className = 'aic-preview-page-header';
          header.innerHTML = headerTemplate;
          page.appendChild(header);
        }

        const content = document.createElement('div');
        content.className = 'aic-preview-page-content';
        const flowRoot = document.createElement('div');
        flowRoot.className = 'aic-flow-root';
        content.appendChild(flowRoot);
        page.appendChild(content);
        wrapper.appendChild(page);
        return { page, content, target: flowRoot };
      }

      function createPageWithRoot(wrapper, headerTemplate, rootMeta){
        const base = createPage(wrapper, headerTemplate);
        if (!rootMeta || !rootMeta.tagName) return base;

        const flowRoot = document.createElement(rootMeta.tagName);
        if (rootMeta.className) flowRoot.className = rootMeta.className;
        if (rootMeta.styleText) flowRoot.setAttribute('style', rootMeta.styleText);
        base.target.appendChild(flowRoot);
        return { ...base, target: flowRoot };
      }

      function paginate(){
        const body = document.body;
        if (!body) return;

        const headerNode = body.querySelector('.aic-page-header');
        const headerTemplate = headerNode ? headerNode.innerHTML : '';
        if (headerNode && headerNode.parentNode) headerNode.parentNode.removeChild(headerNode);

        const explicitContentRoot = body.querySelector('.aic-page-content');
        const sourceRoot = explicitContentRoot || body;
        let nodes = normalizeNodes(Array.from(sourceRoot.childNodes));
        let rootMeta = null;

        if (!explicitContentRoot && nodes.length === 1 && isElement(nodes[0])) {
          const singleRoot = nodes[0];
          const rootChildren = normalizeNodes(Array.from(singleRoot.childNodes));
          if (rootChildren.length > 0) {
            rootMeta = {
              tagName: String(singleRoot.tagName || 'div').toLowerCase(),
              className: singleRoot.className || '',
              styleText: singleRoot.getAttribute('style') || '',
            };
            nodes = rootChildren;
          }
        }

        nodes = expandPaginatedNodes(nodes);

        const wrapper = document.createElement('div');
        wrapper.className = 'aic-preview-pages';

        body.innerHTML = '';
        body.appendChild(wrapper);

        if (nodes.length === 0) {
          createPageWithRoot(wrapper, headerTemplate, rootMeta);
          return;
        }

        let current = createPageWithRoot(wrapper, headerTemplate, rootMeta);

        for (const node of nodes) {
          if (forcePageBreak(node)) {
            current = createPageWithRoot(wrapper, headerTemplate, rootMeta);
            continue;
          }

          if (isPaginatedTable(node)) {
            current = paginateTableNode(node, wrapper, headerTemplate, rootMeta, current);
            continue;
          }

          current.target.appendChild(node);

          const maxHeight = current.content.clientHeight;
          const currentHeight = current.content.scrollHeight;
          if (currentHeight <= maxHeight + 1) continue;

          current.target.removeChild(node);
          current = createPageWithRoot(wrapper, headerTemplate, rootMeta);
          current.target.appendChild(node);

          if (isKeepTogether(node) && current.content.scrollHeight > current.content.clientHeight + 1) {
            node.style.breakInside = 'auto';
            node.style.pageBreakInside = 'auto';
          }
        }

        const pages = Array.from(wrapper.querySelectorAll('.aic-preview-page'));
        const total = pages.length;
        pages.forEach((page, index) => {
          const badge = document.createElement('div');
          badge.className = 'aic-preview-page-number';
          badge.textContent = 'Página ' + (index + 1) + '/' + total;
          page.appendChild(badge);
        });
      }

      window.addEventListener('load', function(){
        requestAnimationFrame(function(){
          requestAnimationFrame(paginate);
        });
      });
    })();
  <\/script>`;

  if (/<html[\s>]/i.test(content)) {
    if (/<head[\s>]/i.test(content)) {
      return content
        .replace(/<html(.*?)>/i, `<html$1 data-aic-paper="${paperSize}">`)
        .replace(/<head(.*?)>/i, `<head$1><style>${previewCss}</style>${paginationScript}`);
    }
    return content.replace(/<html(.*?)>/i, `<html$1 data-aic-paper="${paperSize}"><head><style>${previewCss}</style>${paginationScript}</head>`);
  }

  return `<!doctype html><html data-aic-paper="${paperSize}"><head><style>${previewCss}</style>${paginationScript}</head><body>${content}</body></html>`;
}

function escapeBlockText(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function createVisualBlock(type, partial = {}) {
  const id = `${type}_${Math.random().toString(36).slice(2, 10)}`;
  const base = {
    flexContainer: {
      direction: 'row',
      wrap: 'wrap',
      justify: 'flex-start',
      align: 'stretch',
      alignContent: 'stretch',
      gap: 12,
      width: '',
      height: '',
      minHeight: '',
      padding: '12px',
      margin: '0 0 12px',
      background: '',
      borderRadius: '8px',
      css: '',
    },
    heading: { text: 'Section title', level: 2, align: 'left', background: '', css: '' },
    text: { text: 'Write your paragraph here...', background: '', css: '' },
    textSingleLine: { text: 'Text: {{invoice.number}}', align: 'left', background: '', css: '' },
    conditional: {
      condition: 'invoice.number',
      thenText: 'Text: {{invoice.number}}',
      elseText: '',
      background: '',
      css: '',
    },
    button: { label: 'Call to action', href: '#', align: 'left', background: '', css: '' },
    image: {
      src: '',
      alt: '',
      width: '100%',
      height: 'auto',
      alignSelf: 'auto',
      objectFit: 'contain',
      objectPosition: 'center center',
      margin: '0',
      background: '',
      css: '',
    },
    columns2: { left: 'Left column content', right: 'Right column content', background: '', css: '' },
    list: { source: 'items', ordered: false, itemTemplate: '{{this}}', emptyText: 'No items', background: '', css: '' },
    table: {
      source: 'items',
      columns: [
        { header: 'Name', value: '{{this.name}}' },
        { header: 'Value', value: '{{this.value}}' },
      ],
      emptyText: 'No rows',
      background: '',
      css: '',
    },
    divider: {},
    spacer: { height: 24, css: '' },
    raw: { html: '<div>Custom HTML block</div>', css: '' },
  };
  const block = {
    id,
    type,
    props: { ...(base[type] || {}), ...(partial.props || partial) },
    children: Array.isArray(partial.children) ? partial.children : [],
  };
  if (type !== 'flexContainer') {
    block.children = [];
  }
  return block;
}

function extractSampleDataPaths(value, prefix = '') {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const indexed = value.slice(0, 8).flatMap((item, index) => {
      const path = `${prefix}[${index}]`;
      return extractSampleDataPaths(item, path);
    });
    return prefix ? [{ path: prefix, label: `${prefix} (array)` }, ...indexed] : indexed;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    const nested = entries.flatMap(([key, child]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      return extractSampleDataPaths(child, path);
    });
    return prefix ? [{ path: prefix, label: `${prefix} (object)` }, ...nested] : nested;
  }
  if (!prefix) return [];
  return [{ path: prefix, label: `${prefix} (${typeof value})` }];
}

function extractArrayPaths(value, prefix = '') {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const nested = value.slice(0, 8).flatMap((item, index) => extractArrayPaths(item, `${prefix}[${index}]`));
    return prefix ? [{ path: prefix, label: `${prefix} (array)` }, ...nested] : nested;
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, child]) => {
      const next = prefix ? `${prefix}.${key}` : key;
      return extractArrayPaths(child, next);
    });
  }
  return [];
}

function normalizeTableColumns(columns) {
  const normalized = Array.isArray(columns)
    ? columns
      .map((column) => ({
        header: String(column?.header || '').trim(),
        value: String(column?.value || '').trim(),
      }))
      .filter((column) => column.header || column.value)
    : [];
  if (normalized.length > 0) return normalized;
  return [
    { header: 'Name', value: '{{this.name}}' },
    { header: 'Value', value: '{{this.value}}' },
  ];
}

function getBindingPathFromValue(value) {
  const match = String(value || '').trim().match(/^\{\{\s*([a-zA-Z0-9_.\[\]]+)\s*\}\}$/);
  return match ? match[1] : null;
}

function toHandlebarsBinding(path) {
  return `{{${String(path || '').trim()}}}`;
}

function getAssetNameFromValue(value) {
  const match = String(value || '').trim().match(/^\{\{\{?\s*(assetDataUri|asset)\s+['"]([^'"]+)['"]\s*\}\}?$/i);
  if (!match) return null;
  return match[2] || null;
}

function isAssetBindingValue(value) {
  return /^\{\{\{?\s*(assetDataUri|asset)\s+['"][^'"]+['"]\s*\}\}?$/i.test(String(value || '').trim());
}

function isAssetDataUriBindingValue(value) {
  return /^\{\{\{?\s*assetDataUri\s+['"][^'"]+['"]\s*\}\}?$/i.test(String(value || '').trim());
}

function toAssetDataUriBinding(assetName) {
  return `{{{assetDataUri '${String(assetName || '').trim()}'}}}`;
}

function toLegacyAssetBinding(assetName) {
  return `{{asset '${String(assetName || '').trim()}'}}`;
}

function normalizeImageAssetBinding(value) {
  const name = getAssetNameFromValue(value);
  if (!name) return value;
  return toAssetDataUriBinding(name);
}

function toAssetBinding(assetName) {
  return toAssetDataUriBinding(assetName);
}

function normalizeImageSrcValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (isAssetBindingValue(raw)) return normalizeImageAssetBinding(raw);
  return raw;
}

function getInlineBindingQuery(value) {
  const raw = String(value || '');
  const match = raw.match(/\{\{\s*([a-zA-Z0-9_.\[\]]*)$/);
  if (!match) return null;
  return match[1] || '';
}

function applyInlineBinding(value, path) {
  const raw = String(value || '');
  return raw.replace(/\{\{\s*[a-zA-Z0-9_.\[\]]*$/, `{{${path}}}`);
}

function toColorPickerValue(value) {
  const normalized = String(value || '').trim();
  if (/^#[0-9a-f]{3}$/i.test(normalized)) {
    const expanded = normalized.slice(1).split('').map((c) => `${c}${c}`).join('');
    return `#${expanded}`;
  }
  if (/^#[0-9a-f]{6}$/i.test(normalized)) return normalized;
  return '#2563eb';
}

function normalizeVisualBlock(block) {
  if (!block || typeof block !== 'object') return createVisualBlock('raw');
  const type = VISUAL_BLOCK_TYPES.some((item) => item.type === block.type) ? block.type : 'raw';
  const incomingProps = block.props || {};
  const normalizedProps = type === 'image'
    ? { ...incomingProps, src: normalizeImageSrcValue(incomingProps.src || '') }
    : incomingProps;
  const normalized = createVisualBlock(type, {
    props: normalizedProps,
    children: Array.isArray(block.children) ? block.children.map(normalizeVisualBlock) : [],
  });
  if (type === 'flexContainer' && String(normalized.props?.width || '').trim() === '100%') {
    normalized.props.width = '';
  }
  if (block.id) normalized.id = String(block.id);
  return normalized;
}

function normalizeVisualBlocks(blocks) {
  if (!Array.isArray(blocks)) return [];
  return blocks.map(normalizeVisualBlock);
}

function mergeInlineStyle(baseStyle, customCss) {
  const base = String(baseStyle || '').trim().replace(/;+$/, '');
  const extra = String(customCss || '').trim().replace(/;+$/, '');
  if (base && extra) return `${base};${extra}`;
  return base || extra;
}

function parseInlineCssDeclarations(cssText) {
  const result = {};
  String(cssText || '')
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator <= 0) return;
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      if (!property) return;
      result[property] = value;
    });
  return result;
}

function stringifyInlineCssDeclarations(cssMap) {
  return Object.entries(cssMap || {})
    .map(([property, value]) => {
      const safeProperty = String(property || '').trim();
      const safeValue = String(value || '').trim();
      if (!safeProperty || !safeValue) return '';
      return `${safeProperty}:${safeValue}`;
    })
    .filter(Boolean)
    .join(';');
}

function escapeInlineStyle(value) {
  return escapeBlockText(String(value || '').replace(/"/g, '&quot;'));
}

function getBlockDefaultMargin(inFlex, defaultMargin = '0 0 12px') {
  return inFlex ? '0' : defaultMargin;
}

function formatHtmlAttributeValue(value) {
  const raw = String(value ?? '');
  if (/\{\{[\s\S]*\}\}/.test(raw)) return raw;
  return escapeBlockText(raw);
}

function blockContainsId(block, targetId) {
  if (!block || !targetId) return false;
  if (block.id === targetId) return true;
  const children = Array.isArray(block.children) ? block.children : [];
  return children.some((child) => blockContainsId(child, targetId));
}

function findVisualBlockById(blocks, targetId) {
  const list = Array.isArray(blocks) ? blocks : [];
  for (const block of list) {
    if (block.id === targetId) return block;
    const foundInChildren = findVisualBlockById(block.children || [], targetId);
    if (foundInChildren) return foundInChildren;
  }
  return null;
}

function mapVisualBlocks(blocks, mapper) {
  return (Array.isArray(blocks) ? blocks : []).map((block) => {
    const nextChildren = mapVisualBlocks(block.children || [], mapper);
    return mapper({ ...block, children: nextChildren });
  });
}

function removeVisualBlockById(blocks, targetId) {
  let removed = null;
  const walk = (items) => (Array.isArray(items) ? items : []).reduce((acc, block) => {
    if (block.id === targetId) {
      removed = block;
      return acc;
    }
    const nextChildren = walk(block.children || []);
    acc.push({ ...block, children: nextChildren });
    return acc;
  }, []);
  return { blocks: walk(blocks), removed };
}

function appendChildToContainer(blocks, containerId, child) {
  return mapVisualBlocks(blocks, (block) => {
    if (block.id !== containerId || block.type !== 'flexContainer') return block;
    return { ...block, children: [...(block.children || []), child] };
  });
}

function insertChildToContainer(blocks, containerId, child, targetIndex = null) {
  return mapVisualBlocks(blocks, (block) => {
    if (block.id !== containerId || block.type !== 'flexContainer') return block;
    const children = [...(block.children || [])];
    const safeIndex = targetIndex === null
      ? children.length
      : Math.max(0, Math.min(Number(targetIndex) || 0, children.length));
    children.splice(safeIndex, 0, child);
    return { ...block, children };
  });
}

function renderVisualBlockHtml(block, context = {}) {
  const inFlex = Boolean(context?.inFlex);
  const props = block?.props || {};
  const customCss = props.css || '';
  if (block.type === 'flexContainer') {
    const gap = Math.max(0, Number(props.gap || 0));
    const style = mergeInlineStyle(
      `display:flex;flex-direction:${escapeBlockText(props.direction || 'row')};flex-wrap:${escapeBlockText(props.wrap || 'wrap')};justify-content:${escapeBlockText(props.justify || 'flex-start')};align-items:${escapeBlockText(props.align || 'stretch')};align-content:${escapeBlockText(props.alignContent || 'stretch')};gap:${gap}px;${props.width ? `width:${escapeBlockText(props.width)};` : ''}${props.height ? `height:${escapeBlockText(props.height)};` : ''}${props.minHeight ? `min-height:${escapeBlockText(props.minHeight)};` : ''}padding:${escapeBlockText(props.padding || '0')};margin:${escapeBlockText(props.margin || '0 0 12px')};${props.background ? `background:${escapeBlockText(props.background)};` : ''}${props.borderRadius ? `border-radius:${escapeBlockText(props.borderRadius)};` : ''}`,
      customCss,
    );
    const childrenHtml = (Array.isArray(block.children) ? block.children : []).map((child) => renderVisualBlockHtml(child, { inFlex: true })).join('\n');
    return `<div style="${escapeInlineStyle(style)}">${childrenHtml}</div>`;
  }
  if (block.type === 'heading') {
    const level = Math.min(4, Math.max(1, Number(props.level || 2)));
    const align = ['left', 'center', 'right'].includes(props.align) ? props.align : 'left';
    const style = mergeInlineStyle(`margin:${getBlockDefaultMargin(inFlex)};text-align:${align};${props.background ? `background:${escapeBlockText(props.background)};padding:8px;border-radius:6px;` : ''}`, customCss);
    return `<h${level} style="${escapeInlineStyle(style)}">${escapeBlockText(props.text || '')}</h${level}>`;
  }
  if (block.type === 'text') {
    const style = mergeInlineStyle(`margin:${getBlockDefaultMargin(inFlex)};line-height:1.6;white-space:pre-wrap;${props.background ? `background:${escapeBlockText(props.background)};padding:8px;border-radius:6px;` : ''}`, customCss);
    return `<p style="${escapeInlineStyle(style)}">${escapeBlockText(props.text || '')}</p>`;
  }
  if (block.type === 'textSingleLine') {
    const align = ['left', 'center', 'right'].includes(props.align) ? props.align : 'left';
    const style = mergeInlineStyle(`margin:${getBlockDefaultMargin(inFlex)};text-align:${align};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;${props.background ? `background:${escapeBlockText(props.background)};padding:8px;border-radius:6px;` : ''}`, customCss);
    return `<p style="${escapeInlineStyle(style)}">${escapeBlockText(props.text || '')}</p>`;
  }
  if (block.type === 'conditional') {
    const condition = String(props.condition || '').trim() || 'this';
    const thenText = String(props.thenText || '');
    const elseText = String(props.elseText || '');
    const style = mergeInlineStyle(`margin:${getBlockDefaultMargin(inFlex)};white-space:pre-wrap;${props.background ? `background:${escapeBlockText(props.background)};padding:8px;border-radius:6px;` : ''}`, customCss);
    if (elseText) {
      return `<div style="${escapeInlineStyle(style)}">{{#if ${escapeBlockText(condition)}}}${thenText}{{else}}${elseText}{{/if}}</div>`;
    }
    return `<div style="${escapeInlineStyle(style)}">{{#if ${escapeBlockText(condition)}}}${thenText}{{/if}}</div>`;
  }
  if (block.type === 'button') {
    const align = ['left', 'center', 'right'].includes(props.align) ? props.align : 'left';
    const outerStyle = mergeInlineStyle(`margin:${getBlockDefaultMargin(inFlex)};text-align:${align}`, customCss);
    return `<div style="${escapeInlineStyle(outerStyle)}"><a href="${formatHtmlAttributeValue(props.href || '#')}" style="display:inline-block;padding:10px 14px;text-decoration:none;border-radius:6px;background:${escapeBlockText(props.background || '#2563eb')};color:#fff;">${escapeBlockText(props.label || 'Button')}</a></div>`;
  }
  if (block.type === 'image') {
    const safeSrc = normalizeImageSrcValue(props.src || '');
    if (!safeSrc) {
      const emptyStyle = mergeInlineStyle(`margin:${getBlockDefaultMargin(inFlex)};color:#9ca3af;font-size:12px`, customCss);
      return `<div style="${escapeInlineStyle(emptyStyle)}">Image URL is empty</div>`;
    }
    const style = mergeInlineStyle(`margin:${escapeBlockText(props.margin || getBlockDefaultMargin(inFlex, '0'))};align-self:${escapeBlockText(props.alignSelf || 'auto')};${props.background ? `background:${escapeBlockText(props.background)};padding:8px;border-radius:6px;` : ''}`, customCss);
    const rawWidth = String(props.width || '').trim();
    const effectiveWidth = rawWidth || '100%';
    const centerStyle = inFlex ? 'margin-left:auto;margin-right:auto;' : '';
    const imageStyle = `width:${escapeBlockText(effectiveWidth)};max-width:100%;height:${escapeBlockText(props.height || 'auto')};display:block;${centerStyle}object-fit:${escapeBlockText(props.objectFit || 'contain')};object-position:${escapeBlockText(props.objectPosition || 'center center')};`;
    return `<div style="${escapeInlineStyle(style)}"><img src="${formatHtmlAttributeValue(safeSrc)}" alt="${formatHtmlAttributeValue(props.alt || '')}" style="${escapeInlineStyle(imageStyle)}" /></div>`;
  }
  if (block.type === 'columns2') {
    const style = mergeInlineStyle(`display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:${getBlockDefaultMargin(inFlex)};${props.background ? `background:${escapeBlockText(props.background)};padding:8px;border-radius:6px;` : ''}`, customCss);
    return `<div style="${escapeInlineStyle(style)}"><div>${escapeBlockText(props.left || '')}</div><div>${escapeBlockText(props.right || '')}</div></div>`;
  }
  if (block.type === 'list') {
    const source = String(props.source || 'items').trim() || 'items';
    const tag = props.ordered ? 'ol' : 'ul';
    const itemTemplate = props.itemTemplate || '{{this}}';
    const style = mergeInlineStyle(`margin:${getBlockDefaultMargin(inFlex)};padding-left:18px;${props.background ? `background:${escapeBlockText(props.background)};padding:8px 8px 8px 26px;border-radius:6px;` : ''}`, customCss);
    const empty = escapeBlockText(props.emptyText || 'No items');
    return `<${tag} style="${escapeInlineStyle(style)}">{{#each ${escapeBlockText(source)}}}<li>${itemTemplate}</li>{{else}}<li>${empty}</li>{{/each}}</${tag}>`;
  }
  if (block.type === 'table') {
    const source = String(props.source || 'items').trim() || 'items';
    const columns = normalizeTableColumns(props.columns);
    const cellStyle = 'border:1px solid #d1d5db;padding:6px 8px;text-align:left;font-size:12px;vertical-align:top;';
    const style = mergeInlineStyle(`width:100%;border-collapse:collapse;margin:${getBlockDefaultMargin(inFlex)};${props.background ? `background:${escapeBlockText(props.background)};` : ''}`, customCss);
    const head = columns.map((column) => `<th style="${cellStyle}background:#f8fafc;">${escapeBlockText(column.header || '')}</th>`).join('');
    const row = columns.map((column) => `<td style="${cellStyle}">${column.value || '{{this}}'}</td>`).join('');
    const empty = escapeBlockText(props.emptyText || 'No rows');
    return `<table class="aic-page-table" style="${escapeInlineStyle(style)}"><thead><tr>${head}</tr></thead><tbody>{{#each ${escapeBlockText(source)}}}<tr>${row}</tr>{{else}}<tr><td colspan="${columns.length}" style="${cellStyle}">${empty}</td></tr>{{/each}}</tbody></table>`;
  }
  if (block.type === 'divider') {
    const style = mergeInlineStyle(`margin:${inFlex ? '0' : '12px 0'};border:0;border-top:1px solid #d1d5db`, customCss);
    return `<hr style="${escapeInlineStyle(style)}" />`;
  }
  if (block.type === 'spacer') {
    const h = Math.max(0, Number(props.height || 0));
    const style = mergeInlineStyle(`height:${h}px`, customCss);
    return `<div style="${escapeInlineStyle(style)}"></div>`;
  }
  if (block.type === 'raw') {
    if (!customCss) return props.html || '';
    return `<div style="${escapeInlineStyle(customCss)}">${props.html || ''}</div>`;
  }
  return '';
}

function serializeVisualBlocks(blocks) {
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  const meta = `${VISUAL_META_PREFIX}${encodeURIComponent(JSON.stringify(safeBlocks))}-->`;
  const body = safeBlocks.map((block) => renderVisualBlockHtml(block)).join('\n');
  const centered = `<div style="max-width:794px;margin:0 auto;">${body}</div>`;
  return `${meta}\n${centered}`;
}

function parseVisualBlocksFromHtml(html) {
  const value = String(html || '');
  const startIndex = value.indexOf(VISUAL_META_PREFIX);
  if (startIndex !== 0) return null;
  const endIndex = value.indexOf('-->', startIndex);
  if (endIndex < 0) return null;
  try {
    const encoded = value.slice(VISUAL_META_PREFIX.length, endIndex);
    const parsed = JSON.parse(decodeURIComponent(encoded));
    if (!Array.isArray(parsed)) return null;
    return normalizeVisualBlocks(parsed);
  } catch {
    return null;
  }
}

export default function TemplateEditor() {
  const t = useT();
  const { showAlert, showConfirm } = useAlert();
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savedSampleData, setSavedSampleData] = useState({});
  const [contentHtml, setContentHtml] = useState('');
  const [helpersJs, setHelpersJs] = useState('');
  const [dataJson, setDataJson] = useState('{}');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewConsoleLogs, setPreviewConsoleLogs] = useState([]);
  const [debugConsoleEnabled, setDebugConsoleEnabled] = useState(true);
  const [debugConsoleCollapsed, setDebugConsoleCollapsed] = useState(false);
  const [previewPaperSize, setPreviewPaperSize] = useState('A4');
  const [activeTab, setActiveTab] = useState('html');
  const [visualBuilderAllowed, setVisualBuilderAllowed] = useState(true);
  const [aiGenerationAllowed, setAiGenerationAllowed] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [pdfRendering, setPdfRendering] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerationMode, setAiGenerationMode] = useState('all');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsSaving, setVersionsSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [visualBlocks, setVisualBlocks] = useState([]);
  const [selectedVisualBlockId, setSelectedVisualBlockId] = useState(null);
  const [activeDropzone, setActiveDropzone] = useState(null);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [imageSourceMode, setImageSourceMode] = useState('custom');
  const htmlEditorRef = useRef(null);
  const visualBuilderInitialized = useRef(false);
  const visualBuilderFeatureWarningShown = useRef(false);
  const [autoPreviewEnabled, setAutoPreviewEnabled] = useState(() => {
    const saved = localStorage.getItem(AUTO_PREVIEW_STORAGE_KEY);
    if (saved === '0') return false;
    if (saved === '1') return true;
    return true;
  });

  const getVisualBlockLabel = (type) => {
    const item = VISUAL_BLOCK_TYPES.find((entry) => entry.type === type);
    if (!item) return type;
    return t(item.labelKey, item.labelFallback);
  };

  const getPlanLimitMessage = (error) => {
    const message = String(error?.message || '');
    if (error?.code === 'BILLING_RENDER_LIMIT' || error?.code === 'BILLING_TEMPLATES_LIMIT') {
      return t('templateEditor.planLimitReached', 'You reached your current plan limit. Please upgrade to a higher plan to continue.');
    }
    if (/render limit|reached your plan|upgrade your plan|BILLING_RENDER_LIMIT/i.test(message)) {
      return t('templateEditor.planLimitReached', 'You reached your current plan limit. Please upgrade to a higher plan to continue.');
    }
    return message || t('templateEditor.renderFailed', 'Render failed');
  };

  const getEffectiveData = () => {
    let parsed = {};
    try {
      parsed = JSON.parse(dataJson || '{}');
    } catch {
      throw new Error(t('templateEditor.invalidSampleData', 'Sample Data must be valid JSON'));
    }

    if (!parsed || (typeof parsed === 'object' && Object.keys(parsed).length === 0)) {
      return savedSampleData || {};
    }

    return parsed;
  };

  useEffect(() => {
    setLoadingTemplate(true);
    setLoadError('');

    api.getTemplate(id)
      .then((tpl) => {
        setTemplate(tpl);
        setContentHtml(tpl.content_html || '');
        setHelpersJs(tpl.helpers_js || '');

        let cfg = {};
        try {
          cfg = typeof tpl.config_json === 'string'
            ? JSON.parse(tpl.config_json || '{}')
            : (tpl.config_json || {});
        } catch {
          cfg = {};
        }
        const sampleData = cfg.sampleData || {};
        setSavedSampleData(sampleData);
        setDataJson(toPrettyJson(sampleData));
      })
      .catch((err) => {
        setLoadError(err.message || t('templateEditor.loadFailed', 'Could not load template'));
      })
      .finally(() => {
        setLoadingTemplate(false);
      });
  }, [id]);

  useEffect(() => {
    let mounted = true;

    api.getMyBilling()
      .then((billing) => {
        if (!mounted) return;
        setVisualBuilderAllowed(billing?.features?.visualBuilder !== false);
        setAiGenerationAllowed(billing?.features?.aiGeneration !== false);
      })
      .catch(() => {
        if (!mounted) return;
        setVisualBuilderAllowed(true);
        setAiGenerationAllowed(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'visual' || visualBuilderAllowed) return;
    setActiveTab('html');
    if (visualBuilderFeatureWarningShown.current) return;
    visualBuilderFeatureWarningShown.current = true;
    showAlert(
      t('templateEditor.visualBuilderPlanRequired', 'Visual Builder is not available in your current plan.'),
      { tone: 'warning', title: t('billing.settingsTitle', 'Validation') },
    );
  }, [activeTab, visualBuilderAllowed, showAlert, t]);

  useEffect(() => {
    if (!aiDialogOpen || aiGenerationAllowed) return;
    setAiDialogOpen(false);
  }, [aiDialogOpen, aiGenerationAllowed]);

  useEffect(() => {
    visualBuilderInitialized.current = false;
    setVisualBlocks([]);
    setSelectedVisualBlockId(null);
  }, [id]);

  useEffect(() => {
    let mounted = true;

    const loadAssetsForSelector = async () => {
      try {
        const pageSize = 200;
        let page = 1;
        let total = 0;
        let collected = [];

        do {
          const res = await api.getAssets({ page, limit: pageSize });
          const items = Array.isArray(res?.items) ? res.items : [];
          total = Number(res?.total || 0);
          collected = [...collected, ...items];
          page += 1;
        } while (collected.length < total && page <= 20);

        if (mounted) setAvailableAssets(collected);
      } catch {
        if (mounted) setAvailableAssets([]);
      }
    };

    loadAssetsForSelector();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'visual') return;
    if (visualBuilderInitialized.current) return;

    const parsed = parseVisualBlocksFromHtml(contentHtml);
    const initialBlocks = parsed && parsed.length > 0
      ? parsed
      : (contentHtml.trim()
        ? [createVisualBlock('raw', { html: contentHtml })]
        : [
            createVisualBlock('flexContainer', {
              props: { direction: 'column', gap: 10, padding: '12px', borderRadius: '8px' },
              children: [createVisualBlock('heading'), createVisualBlock('text')],
            }),
          ]);

    setVisualBlocks(initialBlocks);
    setSelectedVisualBlockId(initialBlocks[0]?.id || null);
    visualBuilderInitialized.current = true;

    const nextSerialized = serializeVisualBlocks(initialBlocks);
    if (nextSerialized !== contentHtml) {
      setContentHtml(nextSerialized);
    }
  }, [activeTab, contentHtml]);

  const updateVisualBlocks = (updater) => {
    setVisualBlocks((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      setContentHtml(serializeVisualBlocks(next));
      return next;
    });
  };

  const insertHtmlSnippet = (snippet) => {
    const editor = htmlEditorRef.current;
    if (editor && typeof editor.executeEdits === 'function') {
      const selection = editor.getSelection();
      const position = editor.getPosition();
      const range = selection || {
        startLineNumber: position?.lineNumber || 1,
        startColumn: position?.column || 1,
        endLineNumber: position?.lineNumber || 1,
        endColumn: position?.column || 1,
      };

      editor.executeEdits('aic-template-editor', [{
        range,
        text: `\n${snippet}\n`,
        forceMoveMarkers: true,
      }]);
      editor.focus();
      return;
    }

    setContentHtml((prev) => `${String(prev || '')}\n${snippet}\n`);
  };

  const handleInsertPrintCss = () => {
    if (String(contentHtml || '').includes('.aic-page-break') && String(contentHtml || '').includes('.aic-keep-together')) {
      showAlert(t('templateEditor.pageBreakCssAlreadyExists', 'Print CSS for page breaks already exists in the template.'), { tone: 'warning' });
      return;
    }
    insertHtmlSnippet(PAGE_BREAK_PRINT_CSS_SNIPPET);
    showAlert(t('templateEditor.pageBreakCssInserted', 'Print CSS inserted.'), { tone: 'success' });
  };

  const handleInsertPageBreak = () => {
    insertHtmlSnippet(PAGE_BREAK_MARKER_SNIPPET);
  };

  const handleInsertPageHeader = () => {
    insertHtmlSnippet(PAGE_HEADER_SNIPPET);
  };

  const handleInsertPageContent = () => {
    insertHtmlSnippet(PAGE_CONTENT_SNIPPET);
  };

  const handleInsertKeepTogether = () => {
    insertHtmlSnippet(KEEP_TOGETHER_SNIPPET);
  };

  const handleInsertPaginatedTable = () => {
    insertHtmlSnippet(PAGINATED_TABLE_SNIPPET);
  };

  const insertVisualBlockAt = (type, index) => {
    const block = createVisualBlock(type);
    updateVisualBlocks((current) => {
      const items = [...current];
      const safeIndex = Math.max(0, Math.min(index, items.length));
      items.splice(safeIndex, 0, block);
      return items;
    });
    setSelectedVisualBlockId(block.id);
  };

  const insertVisualBlockIntoContainer = (containerId, type, targetIndex = null) => {
    const block = createVisualBlock(type);
    updateVisualBlocks((current) => insertChildToContainer(current, containerId, block, targetIndex));
    setSelectedVisualBlockId(block.id);
  };

  const moveVisualBlockToRoot = (blockId, targetIndex) => {
    updateVisualBlocks((current) => {
      const extraction = removeVisualBlockById(current, blockId);
      if (!extraction.removed) return current;
      const items = [...extraction.blocks];
      const safeTarget = Math.max(0, Math.min(targetIndex, items.length));
      items.splice(safeTarget, 0, extraction.removed);
      return items;
    });
  };

  const moveVisualBlockIntoContainer = (blockId, containerId, targetIndex = null) => {
    if (!blockId || !containerId || blockId === containerId) return;
    updateVisualBlocks((current) => {
      const moving = findVisualBlockById(current, blockId);
      const container = findVisualBlockById(current, containerId);
      if (!moving || !container || container.type !== 'flexContainer') return current;
      if (blockContainsId(moving, containerId)) return current;

      const extraction = removeVisualBlockById(current, blockId);
      if (!extraction.removed) return current;

      const containerAfterRemoval = findVisualBlockById(extraction.blocks, containerId);
      if (!containerAfterRemoval || containerAfterRemoval.type !== 'flexContainer') return extraction.blocks;
      const childrenCount = Array.isArray(containerAfterRemoval.children) ? containerAfterRemoval.children.length : 0;
      const safeIndex = targetIndex === null
        ? childrenCount
        : Math.max(0, Math.min(Number(targetIndex) || 0, childrenCount));

      return insertChildToContainer(extraction.blocks, containerId, extraction.removed, safeIndex);
    });
  };

  const handleVisualDrop = (event, targetIndex) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = event.dataTransfer.getData('text/plain') || '';
    if (payload.startsWith('palette:')) {
      insertVisualBlockAt(payload.replace('palette:', ''), targetIndex);
      return;
    }
    if (payload.startsWith('block:')) {
      moveVisualBlockToRoot(payload.replace('block:', ''), targetIndex);
    }
    setActiveDropzone(null);
  };

  const handleVisualDropzoneDragOver = (event, scope, containerId, targetIndex) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveDropzone((prev) => {
      if (
        prev
        && prev.scope === scope
        && prev.containerId === containerId
        && prev.targetIndex === targetIndex
      ) {
        return prev;
      }
      return { scope, containerId, targetIndex };
    });
  };

  const isActiveDropzone = (scope, containerId, targetIndex) => (
    activeDropzone
    && activeDropzone.scope === scope
    && activeDropzone.containerId === containerId
    && activeDropzone.targetIndex === targetIndex
  );

  const handleVisualDropIntoContainer = (event, containerId, targetIndex = null) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = event.dataTransfer.getData('text/plain') || '';
    if (payload.startsWith('palette:')) {
      insertVisualBlockIntoContainer(containerId, payload.replace('palette:', ''), targetIndex);
      return;
    }
    if (payload.startsWith('block:')) {
      moveVisualBlockIntoContainer(payload.replace('block:', ''), containerId, targetIndex);
    }
    setActiveDropzone(null);
  };

  const selectedVisualBlock = findVisualBlockById(visualBlocks, selectedVisualBlockId) || null;
  const sampleDataPaths = useMemo(() => {
    const parsed = parseJsonSafe(dataJson, {});
    return extractSampleDataPaths(parsed);
  }, [dataJson]);
  const sampleArrayPaths = useMemo(() => {
    const parsed = parseJsonSafe(dataJson, {});
    return extractArrayPaths(parsed);
  }, [dataJson]);
  const imageAssets = useMemo(() => {
    const imageExtRe = /\.(png|jpe?g|gif|webp|svg|bmp|ico|avif)$/i;
    return availableAssets
      .filter((asset) => {
        const type = String(asset?.type || '').toLowerCase();
        const name = String(asset?.name || '');
        return Boolean(name) && (type.startsWith('image/') || imageExtRe.test(name));
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [availableAssets]);
  const selectedQuickCssMap = useMemo(() => parseInlineCssDeclarations(selectedVisualBlock?.props?.css || ''), [selectedVisualBlock?.props?.css]);

  useEffect(() => {
    if (!selectedVisualBlock || selectedVisualBlock.type !== 'image') {
      setImageSourceMode('custom');
      return;
    }
    const srcValue = String(selectedVisualBlock?.props?.src || '');
    const assetName = getAssetNameFromValue(srcValue);
    const dataBindingPath = getBindingPathFromValue(srcValue);
    setImageSourceMode(assetName ? 'asset' : (dataBindingPath ? 'data' : 'custom'));
  }, [selectedVisualBlockId, selectedVisualBlock?.type, selectedVisualBlock?.props?.src]);

  useEffect(() => {
    if (!selectedVisualBlock || selectedVisualBlock.type !== 'image') return;
    const currentSrc = String(selectedVisualBlock?.props?.src || '');
    const normalized = normalizeImageSrcValue(currentSrc);
    if (normalized !== currentSrc) {
      updateSelectedVisualProps({ src: normalized });
    }
  }, [selectedVisualBlockId, selectedVisualBlock?.type]);

  const updateSelectedVisualProps = (patch) => {
    if (!selectedVisualBlockId) return;
    updateVisualBlocks((current) => mapVisualBlocks(current, (block) => (
      block.id === selectedVisualBlockId
        ? { ...block, props: { ...(block.props || {}), ...patch } }
        : block
    )));
  };

  const updateSelectedVisualQuickCss = (patch) => {
    const cssMap = { ...parseInlineCssDeclarations(selectedVisualBlock?.props?.css || '') };
    Object.entries(patch || {}).forEach(([property, value]) => {
      const next = String(value ?? '').trim();
      if (!next) {
        delete cssMap[String(property || '').toLowerCase()];
      } else {
        cssMap[String(property || '').toLowerCase()] = next;
      }
    });
    updateSelectedVisualProps({ css: stringifyInlineCssDeclarations(cssMap) });
  };

  const renderQuickCssField = (label, property, options, placeholder = '') => {
    const currentValue = selectedQuickCssMap[property] || '';
    const hasExact = options.some((opt) => opt.value === currentValue);
    return (
      <div className="visual-builder__field">
        {renderCssHelpLabel(label, quickCssHelp[property] || 'Propiedad CSS del bloque.')}
        <select
          value={hasExact ? currentValue : '__custom__'}
          onChange={(e) => {
            const next = e.target.value;
            if (next === '__custom__') return;
            updateSelectedVisualQuickCss({ [property]: next });
          }}
        >
          <option value="__custom__">Custom</option>
          {options.map((option) => (
            <option key={`${property}-${option.value || 'empty'}`} value={option.value}>{option.label}</option>
          ))}
        </select>
        {!hasExact && (
          <input
            value={currentValue}
            onChange={(e) => updateSelectedVisualQuickCss({ [property]: e.target.value })}
            placeholder={placeholder}
          />
        )}
      </div>
    );
  };

  const renderCssHelpLabel = (label, description) => (
    <label className="visual-builder__label-with-help">
      <span>{label}</span>
      <span className="visual-builder__help-icon" title={description} aria-label={description}>?</span>
    </label>
  );

  const quickCssHelp = {
    margin: 'Espacio externo del bloque. Ej: 0 auto centra el bloque horizontalmente.',
    padding: 'Espacio interno entre borde y contenido. Ej: 12px agrega aire interno.',
    width: 'Ancho del elemento. Ej: 100% ocupa todo el ancho disponible.',
    'font-size': 'Tamaño del texto. Ej: 16px para texto base legible.',
    'font-weight': 'Grosor de la tipografía. Ej: 700 para negrita.',
    'line-height': 'Altura entre líneas. Ej: 1.5 mejora lectura en párrafos.',
    'letter-spacing': 'Espacio entre letras. Ej: 0.01em da un ajuste suave.',
    color: 'Color del texto. Ej: #111827 para un tono oscuro.',
    'box-shadow': 'Sombra del bloque. Ej: 0 2px 8px rgba(0,0,0,0.12).',
    border: 'Borde del bloque. Ej: 1px solid #d1d5db.',
    'border-radius': 'Redondeo de esquinas. Ej: 8px.',
    'text-align': 'Alineación del texto. Ej: center centra texto.',
    display: 'Modo de visualización. Ej: flex habilita layout flexible.',
    'justify-content': 'Alinea elementos en el eje principal de flex/grid. Ej: center.',
    'align-items': 'Alinea elementos en el eje cruzado de flex/grid. Ej: center.',
    customCss: 'CSS inline manual. Ej: margin-top:8px;border:1px solid #ddd;',
  };

  const inspectorHelpByLabel = {
    ...quickCssHelp,
    quickcss: 'Atajos de propiedades CSS más usadas para editar rápido el bloque.',
    type: 'Tipo de bloque seleccionado en el inspector.',
    direction: 'Dirección de flex. Ej: row pone elementos en horizontal, column en vertical.',
    wrap: 'Define si los hijos saltan a otra línea. Ej: wrap permite varias filas.',
    'justify content': 'Alinea elementos en el eje principal del contenedor flex.',
    'align items': 'Alinea elementos en el eje cruzado del contenedor flex.',
    'align content': 'Alinea líneas cuando hay múltiples filas/columnas en flex-wrap.',
    'gap (px)': 'Separación entre hijos del contenedor. Ej: 12 crea espacio uniforme.',
    width: 'Ancho del bloque. Ej: 100% ocupa todo el ancho disponible.',
    height: 'Alto del bloque. Ej: auto ajusta al contenido, 120px fija altura.',
    'min height': 'Alto mínimo del bloque. Ej: 80px evita que colapse.',
    padding: 'Espacio interno del bloque entre borde y contenido.',
    margin: 'Espacio externo del bloque respecto a otros elementos.',
    background: 'Color o fondo del bloque. Ej: #2a3650.',
    'border radius': 'Redondea esquinas. Ej: 8px.',
    'add child block': 'Inserta bloques hijos dentro del contenedor flex.',
    text: 'Contenido de texto del bloque. Acepta variables Handlebars.',
    label: 'Texto visible del botón.',
    url: 'Enlace del botón o recurso. Ej: https://... o {{invoice.url}}.',
    level: 'Nivel de título. H1 más grande, H4 más pequeño.',
    align: 'Alineación del contenido textual.',
    condition: 'Condición Handlebars para mostrar el bloque. Ej: invoice.number',
    'then text': 'Texto mostrado cuando la condición es verdadera.',
    'else text': 'Texto mostrado cuando la condición es falsa.',
    source: 'Origen de la imagen: URL/expresión, dato o asset.',
    'data path': 'Ruta del dato para enlazar dinámicamente el valor.',
    'asset image': 'Archivo de imagen cargado desde assets.',
    alt: 'Texto alternativo de la imagen para accesibilidad.',
    'align self': 'Alineación individual del item dentro del flex padre.',
    'object fit': 'Cómo se ajusta la imagen al cuadro: contain, cover, etc.',
    'object position': 'Posición del contenido de imagen dentro del cuadro.',
    left: 'Contenido de la columna izquierda.',
    right: 'Contenido de la columna derecha.',
    'array source': 'Ruta del arreglo para renderizar listas/tabla.',
    style: 'Tipo visual para listas: bullets o numerada.',
    'item template': 'Plantilla por elemento. Ej: Fecha: {{this.fecha}}.',
    'empty text': 'Texto mostrado cuando no hay datos.',
    'cell template': 'Plantilla de celda de tabla. Ej: {{this.value}}.',
    'height (px)': 'Altura en píxeles para el spacer.',
    html: 'HTML libre del bloque Raw HTML.',
    'custom css (inline)': 'CSS inline manual aplicado al bloque.',
  };

  const getInspectorHelpText = (label) => {
    const key = String(label || '').trim().toLowerCase();
    return inspectorHelpByLabel[key] || `Configura ${label}. Ejemplo: ajusta este valor según tu diseño.`;
  };

  const renderInspectorLabel = (label) => renderCssHelpLabel(label, getInspectorHelpText(label));

  const renderQuickCssControls = () => (
    <>
      <div className="visual-builder__field">
        {renderInspectorLabel('Quick CSS')}
        <div className="visual-builder__binding-help">Most-used CSS properties</div>
      </div>
      {renderQuickCssField('Margin', 'margin', [
        { value: '', label: 'None' },
        { value: '0', label: '0' },
        { value: '0 auto', label: '0 auto (horizontal center)' },
        { value: '8px 0', label: '8px 0' },
        { value: '12px 0', label: '12px 0' },
        { value: '0 0 12px', label: '0 0 12px' },
      ], '0 auto')}
      {renderQuickCssField('Padding', 'padding', [
        { value: '', label: 'None' },
        { value: '0', label: '0' },
        { value: '4px', label: '4px' },
        { value: '8px', label: '8px' },
        { value: '12px', label: '12px' },
        { value: '16px', label: '16px' },
        { value: '24px', label: '24px' },
      ], '12px')}
      {renderQuickCssField('Width', 'width', [
        { value: '', label: 'Auto' },
        { value: '100%', label: '100%' },
        { value: '75%', label: '75%' },
        { value: '50%', label: '50%' },
        { value: '33.333%', label: '33.333%' },
        { value: '25%', label: '25%' },
      ], '100%')}
      {renderQuickCssField('Font size', 'font-size', [
        { value: '', label: 'Default' },
        { value: '12px', label: '12px' },
        { value: '14px', label: '14px' },
        { value: '16px', label: '16px' },
        { value: '18px', label: '18px' },
        { value: '20px', label: '20px' },
      ], '16px')}
      {renderQuickCssField('Font weight', 'font-weight', [
        { value: '', label: 'Default' },
        { value: '400', label: 'Regular (400)' },
        { value: '500', label: 'Medium (500)' },
        { value: '600', label: 'Semibold (600)' },
        { value: '700', label: 'Bold (700)' },
        { value: '800', label: 'Extra Bold (800)' },
      ], '600')}
      {renderQuickCssField('Line height', 'line-height', [
        { value: '', label: 'Default' },
        { value: '1', label: '1' },
        { value: '1.2', label: '1.2' },
        { value: '1.4', label: '1.4' },
        { value: '1.5', label: '1.5' },
        { value: '1.75', label: '1.75' },
      ], '1.5')}
      {renderQuickCssField('Letter spacing', 'letter-spacing', [
        { value: '', label: 'Default' },
        { value: '-0.02em', label: '-0.02em' },
        { value: '-0.01em', label: '-0.01em' },
        { value: '0', label: '0' },
        { value: '0.01em', label: '0.01em' },
        { value: '0.03em', label: '0.03em' },
      ], '0.01em')}
      {renderQuickCssField('Color', 'color', [
        { value: '', label: 'Default' },
        { value: '#111827', label: '#111827' },
        { value: '#1f2937', label: '#1f2937' },
        { value: '#374151', label: '#374151' },
        { value: '#ffffff', label: '#ffffff' },
        { value: '#2563eb', label: '#2563eb' },
      ], '#111827')}
      {renderQuickCssField('Box shadow', 'box-shadow', [
        { value: '', label: 'None' },
        { value: '0 1px 2px rgba(0,0,0,0.06)', label: 'Soft' },
        { value: '0 2px 8px rgba(0,0,0,0.12)', label: 'Medium' },
        { value: '0 4px 14px rgba(0,0,0,0.16)', label: 'Strong' },
        { value: 'inset 0 1px 2px rgba(0,0,0,0.08)', label: 'Inset' },
      ], '0 2px 8px rgba(0,0,0,0.12)')}
      {renderQuickCssField('Border', 'border', [
        { value: '', label: 'None' },
        { value: '1px solid #d1d5db', label: '1px solid #d1d5db' },
        { value: '1px solid #9ca3af', label: '1px solid #9ca3af' },
        { value: '2px solid #d1d5db', label: '2px solid #d1d5db' },
        { value: '1px dashed #9ca3af', label: '1px dashed #9ca3af' },
        { value: '1px solid #2563eb', label: '1px solid #2563eb' },
      ], '1px solid #d1d5db')}
      {renderQuickCssField('Border radius', 'border-radius', [
        { value: '', label: 'None' },
        { value: '4px', label: '4px' },
        { value: '6px', label: '6px' },
        { value: '8px', label: '8px' },
        { value: '12px', label: '12px' },
        { value: '999px', label: 'Full (pill)' },
      ], '8px')}
      {renderQuickCssField('Text align', 'text-align', [
        { value: '', label: 'Default' },
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
        { value: 'justify', label: 'Justify' },
      ], 'center')}
      {renderQuickCssField('Display', 'display', [
        { value: '', label: 'Default' },
        { value: 'block', label: 'block' },
        { value: 'inline-block', label: 'inline-block' },
        { value: 'inline', label: 'inline' },
        { value: 'flex', label: 'flex' },
        { value: 'grid', label: 'grid' },
      ], 'block')}
      {renderQuickCssField('Justify content', 'justify-content', [
        { value: '', label: 'Default' },
        { value: 'flex-start', label: 'Start' },
        { value: 'center', label: 'Center' },
        { value: 'flex-end', label: 'End' },
        { value: 'space-between', label: 'Space between' },
        { value: 'space-around', label: 'Space around' },
        { value: 'space-evenly', label: 'Space evenly' },
      ], 'center')}
      {renderQuickCssField('Align items', 'align-items', [
        { value: '', label: 'Default' },
        { value: 'stretch', label: 'Stretch' },
        { value: 'flex-start', label: 'Start' },
        { value: 'center', label: 'Center' },
        { value: 'flex-end', label: 'End' },
      ], 'center')}
    </>
  );

  const removeSelectedVisualBlock = () => {
    if (!selectedVisualBlockId) return;
    updateVisualBlocks((current) => removeVisualBlockById(current, selectedVisualBlockId).blocks);
    setSelectedVisualBlockId(null);
  };

  const renderArraySourceField = (label, propKey = 'source') => {
    const source = String(selectedVisualBlock?.props?.[propKey] || '');
    const hasExact = sampleArrayPaths.some((item) => item.path === source);
    return (
      <div className="visual-builder__field">
        {renderInspectorLabel(label)}
        <select
          value={hasExact ? source : '__custom__'}
          onChange={(e) => {
            const next = e.target.value;
            if (next === '__custom__') return;
            updateSelectedVisualProps({ [propKey]: next });
          }}
        >
          <option value="__custom__">{t('templateEditor.visualCustomSource', 'Custom array path')}</option>
          {sampleArrayPaths.map((item) => (
            <option key={`arr-${item.path}`} value={item.path}>{item.label}</option>
          ))}
        </select>
        {!hasExact && (
          <input
            value={source}
            onChange={(e) => updateSelectedVisualProps({ [propKey]: e.target.value })}
            placeholder="items"
          />
        )}
      </div>
    );
  };

  const updateSelectedTableColumn = (columnIndex, patch) => {
    const columns = normalizeTableColumns(selectedVisualBlock?.props?.columns);
    const next = columns.map((column, idx) => (idx === columnIndex ? { ...column, ...patch } : column));
    updateSelectedVisualProps({ columns: next });
  };

  const addSelectedTableColumn = () => {
    const columns = normalizeTableColumns(selectedVisualBlock?.props?.columns);
    updateSelectedVisualProps({
      columns: [...columns, { header: `Column ${columns.length + 1}`, value: '{{this}}' }],
    });
  };

  const removeSelectedTableColumn = (columnIndex) => {
    const columns = normalizeTableColumns(selectedVisualBlock?.props?.columns);
    const next = columns.filter((_, idx) => idx !== columnIndex);
    updateSelectedVisualProps({ columns: next.length > 0 ? next : [{ header: 'Value', value: '{{this}}' }] });
  };

  const renderBackgroundField = (label = 'Background') => (
    <div className="visual-builder__field">
      {renderInspectorLabel(label)}
      <div className="visual-builder__field-row">
        <input
          type="color"
          value={toColorPickerValue(selectedVisualBlock?.props?.background || '')}
          onChange={(e) => updateSelectedVisualProps({ background: e.target.value })}
          style={{ padding: 2, width: 46, minWidth: 46, height: 34 }}
        />
        <input
          value={selectedVisualBlock?.props?.background || ''}
          onChange={(e) => updateSelectedVisualProps({ background: e.target.value })}
          placeholder="#2563eb or rgba(0,0,0,.2)"
        />
      </div>
    </div>
  );

  const renderBindableTextField = (label, propKey, { multiline = false, rows = 4, placeholder = '' } = {}) => {
    const currentValue = String(selectedVisualBlock?.props?.[propKey] || '');
    const bindingPath = getBindingPathFromValue(currentValue);
    const isBound = Boolean(bindingPath);
    const renderInlineBindingEditor = () => {
      const inlineQuery = getInlineBindingQuery(currentValue);
      const inlineSuggestions = inlineQuery === null
        ? []
        : sampleDataPaths
          .filter((item) => item.path.toLowerCase().includes(inlineQuery.toLowerCase()))
          .slice(0, 10);

      return (
        <>
          {multiline ? (
            <textarea
              rows={rows}
              value={currentValue}
              onChange={(e) => updateSelectedVisualProps({ [propKey]: e.target.value })}
              placeholder={placeholder}
            />
          ) : (
            <input
              value={currentValue}
              onChange={(e) => updateSelectedVisualProps({ [propKey]: e.target.value })}
              placeholder={placeholder}
            />
          )}
          {inlineQuery !== null && (
            <div className="visual-builder__binding-help">
              {t('templateEditor.visualBindingHint', 'Tip: escribe {{ para insertar una variable')}
            </div>
          )}
          {inlineSuggestions.length > 0 && (
            <div className="visual-builder__binding-suggestions">
              {inlineSuggestions.map((item) => (
                <button
                  key={`${propKey}-${item.path}-inline`}
                  type="button"
                  className="visual-builder__binding-option"
                  onClick={() => updateSelectedVisualProps({ [propKey]: applyInlineBinding(currentValue, item.path) })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </>
      );
    };

    return (
      <div className="visual-builder__field">
        {renderInspectorLabel(label)}
        <select
          value={isBound ? bindingPath : '__custom__'}
          onChange={(e) => {
            const next = e.target.value;
            if (next === '__custom__') {
              updateSelectedVisualProps({ [propKey]: '' });
              return;
            }
            updateSelectedVisualProps({ [propKey]: toHandlebarsBinding(next) });
          }}
        >
          <option value="__custom__">{t('templateEditor.visualCustomText', 'Custom text')}</option>
          {sampleDataPaths.map((item) => (
            <option key={`${propKey}-${item.path}`} value={item.path}>{item.label}</option>
          ))}
        </select>

        {isBound ? (
          <div className="visual-builder__binding-preview">{t('templateEditor.visualBoundTo', 'Bound to')}: {bindingPath}</div>
        ) : renderInlineBindingEditor()}
      </div>
    );
  };

  const renderInlineBindingInput = (label, value, onChange, { multiline = false, rows = 3, placeholder = '' } = {}) => {
    const currentValue = String(value || '');
    const inlineQuery = getInlineBindingQuery(currentValue);
    const inlineSuggestions = inlineQuery === null
      ? []
      : sampleDataPaths
        .filter((item) => item.path.toLowerCase().includes(inlineQuery.toLowerCase()))
        .slice(0, 10);

    return (
      <div className="visual-builder__field">
        {renderInspectorLabel(label)}
        {multiline ? (
          <textarea rows={rows} value={currentValue} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        ) : (
          <input value={currentValue} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        )}
        {inlineQuery !== null && (
          <div className="visual-builder__binding-help">
            {t('templateEditor.visualBindingHint', 'Tip: escribe {{ para insertar una variable')}
          </div>
        )}
        {inlineSuggestions.length > 0 && (
          <div className="visual-builder__binding-suggestions">
            {inlineSuggestions.map((item) => (
              <button
                key={`${label}-${item.path}-inline`}
                type="button"
                className="visual-builder__binding-option"
                onClick={() => onChange(applyInlineBinding(currentValue, item.path))}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderImageSourceField = () => {
    const srcValue = String(selectedVisualBlock?.props?.src || '');
    const assetName = getAssetNameFromValue(srcValue);
    const dataBindingPath = getBindingPathFromValue(srcValue);
    const sourceMode = imageSourceMode;
    const hasKnownAsset = assetName ? imageAssets.some((asset) => asset.name === assetName) : false;

    return (
      <>
        <div className="visual-builder__field">
          {renderInspectorLabel(t('templateEditor.visualSource', 'Source'))}
          <select
            value={sourceMode}
            onChange={(e) => {
              const next = e.target.value;
              setImageSourceMode(next);
              if (next === 'custom') {
                updateSelectedVisualProps({ src: '' });
                return;
              }
              if (next === 'data') {
                const fallbackPath = sampleDataPaths[0]?.path || '';
                updateSelectedVisualProps({ src: fallbackPath ? toHandlebarsBinding(fallbackPath) : '' });
                return;
              }
              const fallbackAsset = imageAssets[0]?.name || '';
              updateSelectedVisualProps({ src: fallbackAsset ? toAssetBinding(fallbackAsset) : '' });
            }}
          >
            <option value="custom">{t('templateEditor.visualSourceCustom', 'Custom URL / expression')}</option>
            <option value="data">{t('templateEditor.visualSourceData', 'Sample data binding')}</option>
            <option value="asset">{t('templateEditor.visualSourceAsset', 'Asset')}</option>
          </select>
        </div>

        {sourceMode === 'custom' && (
          renderInlineBindingInput(t('templateEditor.visualUrl', 'URL'), srcValue, (next) => updateSelectedVisualProps({ src: next }), { placeholder: t('templateEditor.visualUrlOrBindingPlaceholder', 'https://... or {{...}}') })
        )}

        {sourceMode === 'data' && (
          <div className="visual-builder__field">
            {renderInspectorLabel(t('templateEditor.visualDataPath', 'Data path'))}
            <select
              value={dataBindingPath || '__custom__'}
              onChange={(e) => {
                const next = e.target.value;
                if (next === '__custom__') {
                  updateSelectedVisualProps({ src: '' });
                  return;
                }
                updateSelectedVisualProps({ src: toHandlebarsBinding(next) });
              }}
            >
              <option value="__custom__">{t('templateEditor.visualCustomPath', 'Custom path')}</option>
              {sampleDataPaths.map((item) => (
                <option key={`img-data-${item.path}`} value={item.path}>{item.label}</option>
              ))}
            </select>
            <input
              value={dataBindingPath || ''}
              onChange={(e) => updateSelectedVisualProps({ src: e.target.value ? toHandlebarsBinding(e.target.value) : '' })}
              placeholder={t('templateEditor.visualLogoPathPlaceholder', 'invoice.logoUrl')}
            />
          </div>
        )}

        {sourceMode === 'asset' && (
          <div className="visual-builder__field">
            {renderInspectorLabel(t('templateEditor.visualAssetImage', 'Asset image'))}
            <select
              value={hasKnownAsset ? assetName : '__none__'}
              onChange={(e) => {
                const next = e.target.value;
                if (next === '__none__') {
                  updateSelectedVisualProps({ src: '' });
                  return;
                }
                updateSelectedVisualProps({ src: toAssetBinding(next) });
              }}
            >
              <option value="__none__">{t('templateEditor.visualSelectAsset', 'Select an asset...')}</option>
              {imageAssets.map((asset) => (
                <option key={`img-asset-${asset.id || asset.name}`} value={asset.name}>{asset.name}{asset.__score === 1 ? ' (non-image)' : ''}</option>
              ))}
            </select>
            {imageAssets.length === 0 && (
              <div className="visual-builder__binding-help">{t('templateEditor.visualNoAssetsFound', 'No assets found. Upload assets first.')}</div>
            )}
          </div>
        )}
      </>
    );
  };

  const renderVisualBlockPreview = (block) => {
    if (block.type === 'flexContainer') {
      return (
        <div className="visual-builder__container-summary">
          <span>{t('templateEditor.visualFlexDirection', 'Direction')}: {block.props?.direction || 'row'}</span>
          <span>{t('templateEditor.visualGap', 'Gap')}: {block.props?.gap || 0}px</span>
          <span>{t('templateEditor.visualChildren', 'Children')}: {(block.children || []).length}</span>
        </div>
      );
    }
    if (block.type === 'heading') return <h4 style={{ margin: 0 }}>{block.props?.text || ''}</h4>;
    if (block.type === 'text') return <p style={{ margin: 0 }}>{block.props?.text || ''}</p>;
    if (block.type === 'textSingleLine') return <p style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{block.props?.text || ''}</p>;
    if (block.type === 'conditional') return <div className="visual-builder__mini-box">{t('templateEditor.visualIf', 'IF')} {String(block.props?.condition || 'this')}</div>;
    if (block.type === 'button') return <button className="btn-primary" style={{ pointerEvents: 'none' }}>{block.props?.label || ''}</button>;
    if (block.type === 'image') return <span style={{ color: 'var(--text-muted)' }}>{block.props?.src || t('templateEditor.visualImagePlaceholder', 'Image URL not set')}</span>;
    if (block.type === 'columns2') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: '100%' }}>
          <div className="visual-builder__mini-box">{block.props?.left || ''}</div>
          <div className="visual-builder__mini-box">{block.props?.right || ''}</div>
        </div>
      );
    }
    if (block.type === 'list') {
      return (
        <div className="visual-builder__mini-box">
          {(block.props?.ordered ? 'OL' : 'UL')} · {String(block.props?.source || 'items')}
        </div>
      );
    }
    if (block.type === 'table') {
      const count = normalizeTableColumns(block.props?.columns).length;
      return (
        <div className="visual-builder__mini-box">
          {t('templateEditor.visualTableSummary', 'Table · {{count}} cols · {{source}}', { count, source: String(block.props?.source || 'items') })}
        </div>
      );
    }
    if (block.type === 'divider') return <hr style={{ width: '100%', border: 0, borderTop: '1px solid var(--border)' }} />;
    if (block.type === 'spacer') return <span style={{ color: 'var(--text-muted)' }}>{t('templateEditor.visualSpacerHeight', 'Height')}: {block.props?.height || 0}px</span>;
    if (block.type === 'raw') return <code style={{ whiteSpace: 'pre-wrap' }}>{(block.props?.html || '').slice(0, 120)}</code>;
    return null;
  };

  const renderVisualBlockCard = (block, depth = 0) => (
    <div
      key={block.id}
      className={`visual-builder__block ${selectedVisualBlockId === block.id ? 'is-selected' : ''} ${block.type === 'flexContainer' ? 'is-container' : ''}`}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData('text/plain', `block:${block.id}`);
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedVisualBlockId(block.id);
      }}
      style={{ marginLeft: depth > 0 ? `${Math.min(depth, 6) * 12}px` : undefined }}
    >
      <div className="visual-builder__block-head">
        <span className="visual-builder__block-type"><GripVertical size={12} /> {getVisualBlockLabel(block.type)}</span>
      </div>
      <div className="visual-builder__block-body">
        {renderVisualBlockPreview(block)}
      </div>

      {block.type === 'flexContainer' && (
        <div className="visual-builder__container-children">
          <div
            className="visual-builder__dropzone visual-builder__dropzone--inner"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => handleVisualDropIntoContainer(e, block.id, 0)}
          />
          {(block.children || []).map((child, index) => (
            <React.Fragment key={child.id}>
              {renderVisualBlockCard(child, depth + 1)}
              <div
                className="visual-builder__dropzone visual-builder__dropzone--inner"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => handleVisualDropIntoContainer(e, block.id, index + 1)}
              />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );

  const loadVersions = async () => {
    setVersionsLoading(true);
    try {
      const res = await api.getTemplateVersions(id);
      const items = Array.isArray(res?.items) ? res.items : [];
      setVersions(items);
      const published = items.find((v) => v.is_published);
      setActiveVersionId(published?.id || null);
    } catch (err) {
      showAlert(err.message || t('templateEditor.versionsLoadFailed', 'Could not load template versions.'), {
        tone: 'error',
        title: t('billing.loadFailed', 'Failed'),
      });
    } finally {
      setVersionsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    loadVersions();
  }, [id]);

  const persistCurrentTemplate = async () => {
    let parsedSampleData = {};
    try {
      parsedSampleData = JSON.parse(dataJson || '{}');
    } catch {
      throw new Error(t('templateEditor.invalidSampleData', 'Sample Data must be valid JSON'));
    }

    await api.updateTemplate(id, {
      contentHtml,
      helpersJs,
      configJson: { sampleData: parsedSampleData },
    });

    setSavedSampleData(parsedSampleData);
    return parsedSampleData;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistCurrentTemplate();
    } catch (err) {
      showAlert(err.message, { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    } finally {
      setSaving(false);
    }
  };

  const handlePublishCurrent = async () => {
    setVersionsSaving(true);
    try {
      await persistCurrentTemplate();
      const created = await api.publishTemplate(id);
      if (created?.id) setActiveVersionId(created.id);
      await loadVersions();
      showAlert(t('templateEditor.versionCreatedPublished', 'Version created and published.'), {
        tone: 'success',
        title: t('templateEditor.versions', 'Versions'),
      });
    } catch (err) {
      showAlert(err.message || t('templateEditor.versionCreateFailed', 'Could not create version.'), {
        tone: 'error',
        title: t('templateEditor.versions', 'Versions'),
      });
    } finally {
      setVersionsSaving(false);
    }
  };

  const handleRestoreVersion = async (versionId) => {
    const ok = await showConfirm(
      t('templateEditor.versionRestoreConfirm', 'Restore this version into the current template? Unsaved changes will be overwritten.'),
      { title: t('templateEditor.versionRestore', 'Restore version'), tone: 'warning' }
    );
    if (!ok) return;

    setVersionsSaving(true);
    try {
      const tpl = await api.restoreTemplateVersion(id, versionId);
      setTemplate(tpl);
      setContentHtml(tpl.content_html || '');
      setHelpersJs(tpl.helpers_js || '');

      let cfg = {};
      try {
        cfg = typeof tpl.config_json === 'string'
          ? JSON.parse(tpl.config_json || '{}')
          : (tpl.config_json || {});
      } catch {
        cfg = {};
      }
      const sampleData = cfg.sampleData || {};
      setSavedSampleData(sampleData);
      setDataJson(toPrettyJson(sampleData));
      setActiveVersionId(versionId);
      await loadVersions();
      showAlert(t('templateEditor.versionRestored', 'Rollback completed and selected version is now published.'), {
        tone: 'success',
        title: t('templateEditor.versions', 'Versions'),
      });
    } catch (err) {
      showAlert(err.message || t('templateEditor.versionRestoreFailed', 'Could not restore this version.'), {
        tone: 'error',
        title: t('templateEditor.versions', 'Versions'),
      });
    } finally {
      setVersionsSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const data = getEffectiveData();

      const preview = await api.renderPreview({
        template: {
          id,
          engine: 'handlebars',
          recipe: 'html',
          contentHtml,
          helpersJs,
        },
        data,
      }, { debugConsole: debugConsoleEnabled });
      setPreviewHtml(preview.html || '');
      setPreviewConsoleLogs(Array.isArray(preview.consoleLogs) ? preview.consoleLogs : []);
    } catch (err) {
      const friendly = getPlanLimitMessage(err);
      setPreviewHtml(`<!doctype html><html><body><pre style="color:#ff6b6b;padding:12px;white-space:pre-wrap;">${friendly}</pre></body></html>`);
      setPreviewConsoleLogs([]);
    } finally {
      setPreviewing(false);
    }
  };

  useEffect(() => {
    localStorage.setItem(AUTO_PREVIEW_STORAGE_KEY, autoPreviewEnabled ? '1' : '0');
  }, [autoPreviewEnabled]);

  useEffect(() => {
    if (!template) return;
    if (!autoPreviewEnabled) return;

    const timer = setTimeout(() => {
      handlePreview();
    }, 500);

    return () => clearTimeout(timer);
  }, [template, contentHtml, helpersJs, dataJson, autoPreviewEnabled]);

  const handleRenderPdf = async () => {
    setPdfRendering(true);
    try {
      const published = versions.find((v) => v.is_published) || null;
      if (!published) {
        showAlert(t('templateEditor.statusNoPublished', 'No published version'), {
          tone: 'warning',
          title: t('templateEditor.renderFailed', 'Render failed'),
        });
        return;
      }

      const data = getEffectiveData();

      const res = await api.render({
        template: {
          id,
          engine: 'handlebars',
          recipe: 'chrome-pdf',
        },
        data,
        options: {
          profiler: { enabled: true },
          chrome: {
            format: previewPaperSize,
            preferCSSPageSize: true,
          },
        },
      });

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      showAlert(getPlanLimitMessage(err), { tone: 'error', title: t('templateEditor.renderFailed', 'Render failed') });
    } finally {
      setPdfRendering(false);
    }
  };

  // ── AI generation ──
  const handleAIGenerate = async () => {
    if (!aiGenerationAllowed) {
      showAlert(
        t('templateEditor.aiPlanRequired', 'AI generation is not available in your current plan.'),
        { tone: 'warning', title: t('billing.settingsTitle', 'Validation') },
      );
      return;
    }

    const prompt = aiPrompt.trim().slice(0, AI_PROMPT_MAX_CHARS);
    if (!prompt) return;

    const hasContent = contentHtml.trim().length > 0 || helpersJs.trim().length > 0;
    if (hasContent) {
      const ok = await showConfirm(
        t('templateEditor.aiConfirmOverwrite', 'This will replace your current template content. Continue?'),
        { title: t('templateEditor.aiGenerate', 'Generate with AI'), tone: 'warning' }
      );
      if (!ok) return;
    }

    setAiGenerating(true);
    try {
      const result = await api.aiGenerateTemplate(prompt, aiGenerationMode);

      if (aiGenerationMode === 'all' || aiGenerationMode === 'html') {
        setContentHtml(result.contentHtml || '');
      }
      if (aiGenerationMode === 'all' || aiGenerationMode === 'helpers') {
        setHelpersJs(result.helpersJs || '');
      }
      if ((aiGenerationMode === 'all' || aiGenerationMode === 'sampleData') && result.sampleData) {
        setDataJson(toPrettyJson(result.sampleData));
      }

      setAiDialogOpen(false);
      setAiPrompt('');
      showAlert(t('templateEditor.aiApplied', 'AI-generated template applied.'), { tone: 'success', title: 'AI' });
    } catch (err) {
      showAlert(err.message || t('templateEditor.aiFailed', 'AI generation failed.'), { tone: 'error', title: 'AI' });
    } finally {
      setAiGenerating(false);
    }
  };

  // Keyboard shortcut: Ctrl+S
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [contentHtml, helpersJs]);

  if (loadingTemplate) return <div className="page-content">{t('templates.loading', 'Loading...')}</div>;

  if (loadError) {
    return (
      <div className="page-content">
        <div className="card" style={{ maxWidth: 680 }}>
          <h3 style={{ marginBottom: 8 }}>{t('templateEditor.openFailed', 'Could not open template')}</h3>
          <p className="error-text">{loadError}</p>
          <button className="btn-secondary" style={{ marginTop: 12 }} onClick={() => navigate('/templates')}>
            {t('templateEditor.backToTemplates', 'Back to Templates')}
          </button>
        </div>
      </div>
    );
  }

  if (!template) return <div className="page-content">{t('templateEditor.noTemplateSelected', 'No template selected')}</div>;

  const publishedVersion = versions.find((v) => v.is_published) || null;
  const publishedConfig = parseJsonSafe(publishedVersion?.config_json, {});
  const publishedSampleData = publishedConfig?.sampleData ?? {};
  const currentSampleData = parseJsonSafe(dataJson, null);

  const hasInvalidCurrentData = currentSampleData === null;
  const isDraftComparedToPublished = !publishedVersion
    || hasInvalidCurrentData
    || (contentHtml || '') !== (publishedVersion?.content_html || '')
    || (helpersJs || '') !== (publishedVersion?.helpers_js || '')
    || JSON.stringify(currentSampleData || {}) !== JSON.stringify(publishedSampleData || {});

  const statusLabel = isDraftComparedToPublished
    ? t('templateEditor.statusDraft', 'Draft')
    : t('templateEditor.statusPublished', 'Published');

  const statusDetail = publishedVersion
    ? `v${publishedVersion.version_number}`
    : t('templateEditor.statusNoPublished', 'No published version');

  return (
    <>
      <div className="toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2>{template.name}</h2>
          <span
            className={`badge ${isDraftComparedToPublished ? 'badge-warning' : 'badge-success'}`}
            title={isDraftComparedToPublished
              ? t('templateEditor.statusDraftHint', 'There are unpublished changes. Render uses the latest published version.')
              : t('templateEditor.statusPublishedHint', 'Current editor content matches the published version.')}
          >
            {statusLabel} · {statusDetail}
          </span>
        </div>
        <div className="toolbar-actions">
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--text-secondary)',
              fontSize: 12,
              paddingRight: 4,
            }}
            title={t('templateEditor.autoPreviewHint', 'When disabled, preview updates only when you click Preview/Refresh.')}
          >
            <span>{t('templateEditor.autoPreview', 'Auto preview')}</span>
            <span className="switch" style={{ transform: 'scale(0.92)' }}>
              <input
                type="checkbox"
                checked={autoPreviewEnabled}
                onChange={(e) => setAutoPreviewEnabled(e.target.checked)}
                aria-label={t('templateEditor.autoPreview', 'Auto preview')}
              />
              <span className="slider" />
            </span>
          </label>
          <button className="btn-secondary" onClick={() => setAiDialogOpen(true)} disabled={aiGenerating || !aiGenerationAllowed}>
            <Sparkles size={14} style={{ marginRight: 4 }} /> {t('templateEditor.aiGenerate', 'Generate with AI')}
          </button>
          <button className="btn-secondary" onClick={() => setVersionsOpen(true)} disabled={versionsSaving}>
            <History size={14} style={{ marginRight: 4 }} /> {t('templateEditor.versions', 'Versions')}
          </button>
          <button className="btn-secondary" onClick={handlePublishCurrent} disabled={versionsSaving || saving}>
            {versionsSaving ? <span className="inline-spinner" /> : <UploadCloud size={14} style={{ marginRight: 4 }} />}
            {t('templateEditor.publish', 'Publish')}
          </button>
          <button className="btn-secondary" onClick={handlePreview} disabled={previewing || pdfRendering}>
            <Eye size={14} style={{ marginRight: 4 }} /> {t('templateEditor.preview', 'Preview')}
          </button>
          <button className="btn-secondary" onClick={handleRenderPdf} disabled={pdfRendering || previewing}>
            {pdfRendering ? <span className="inline-spinner" /> : <FileText size={14} style={{ marginRight: 4 }} />}
            {pdfRendering ? t('templateEditor.generatingPdf', 'Generating PDF...') : t('templateEditor.renderPdf', 'Render PDF')}
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={14} style={{ marginRight: 4 }} /> {saving ? t('templateEditor.saving', 'Saving...') : t('templateEditor.save', 'Save')}
          </button>
        </div>
      </div>

      <div className="editor-layout">
        {/* Left: Code editors */}
        <div className="editor-pane">
          <div className="tabs">
            {visualBuilderAllowed && (
              <div className={`tab ${activeTab === 'visual' ? 'active' : ''}`} onClick={() => setActiveTab('visual')}>
                {t('templateEditor.tabs.visual', 'Visual Builder')}
              </div>
            )}
            <div className={`tab ${activeTab === 'html' ? 'active' : ''}`} onClick={() => setActiveTab('html')}>
              {t('templateEditor.tabs.html', 'Template HTML')}
            </div>
            <div className={`tab ${activeTab === 'helpers' ? 'active' : ''}`} onClick={() => setActiveTab('helpers')}>
              {t('templateEditor.tabs.helpers', 'Helpers JS')}
            </div>
            <div className={`tab ${activeTab === 'data' ? 'active' : ''}`} onClick={() => setActiveTab('data')}>
              {t('templateEditor.tabs.data', 'Sample Data')}
            </div>
          </div>
          {activeTab === 'html' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-secondary)',
                flexWrap: 'wrap',
              }}
            >
              <button type="button" className="btn-secondary" onClick={handleInsertPrintCss}>
                {t('templateEditor.insertPageBreakCss', 'Insertar CSS de páginas')}
              </button>
              <button type="button" className="btn-secondary" onClick={handleInsertPageHeader}>
                {t('templateEditor.insertPageHeader', 'Insertar encabezado de página')}
              </button>
              <button type="button" className="btn-secondary" onClick={handleInsertPageContent}>
                {t('templateEditor.insertPageContent', 'Insertar bloque de contenido paginado')}
              </button>
              <button type="button" className="btn-secondary" onClick={handleInsertPageBreak}>
                {t('templateEditor.insertPageBreak', 'Insertar salto de página')}
              </button>
              <button type="button" className="btn-secondary" onClick={handleInsertKeepTogether}>
                {t('templateEditor.insertKeepTogether', 'Insertar bloque no separable')}
              </button>
              <button type="button" className="btn-secondary" onClick={handleInsertPaginatedTable}>
                {t('templateEditor.insertPaginatedTable', 'Insertar tabla paginada')}
              </button>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                {t('templateEditor.pageBreakHelp', 'Úsalo para contenido largo/corto: agrega saltos donde lo necesites, marca secciones que no deben cortarse y usa .aic-page-table para partir tablas largas con encabezados repetidos.')}
              </span>
            </div>
          )}
          <div className="pane-content">
            {activeTab === 'visual' && visualBuilderAllowed && (
              <div className="visual-builder">
                <div className="visual-builder__palette">
                  <div className="visual-builder__title">{t('templateEditor.visualPalette', 'Blocks')}</div>
                  <div className="visual-builder__palette-list">
                    {VISUAL_BLOCK_TYPES.map((item) => (
                      <button
                        key={item.type}
                        type="button"
                        className="visual-builder__palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', `palette:${item.type}`)}
                        onClick={() => insertVisualBlockAt(item.type, visualBlocks.length)}
                      >
                        <PlusCircle size={13} /> {t(item.labelKey, item.labelFallback)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="visual-builder__canvas-wrap">
                  <div className="visual-builder__title">{t('templateEditor.visualCanvas', 'Canvas (drag & drop)')}</div>
                  <div className="visual-builder__canvas">
                    <div
                      className={`visual-builder__dropzone ${isActiveDropzone('root', null, 0) ? 'is-active' : ''}`}
                      onDragOver={(e) => handleVisualDropzoneDragOver(e, 'root', null, 0)}
                      onDrop={(e) => handleVisualDrop(e, 0)}
                    />

                    {visualBlocks.map((block, index) => (
                      <React.Fragment key={block.id}>
                        {renderVisualBlockCard(block)}
                        <div
                          className={`visual-builder__dropzone ${isActiveDropzone('root', null, index + 1) ? 'is-active' : ''}`}
                          onDragOver={(e) => handleVisualDropzoneDragOver(e, 'root', null, index + 1)}
                          onDrop={(e) => handleVisualDrop(e, index + 1)}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="visual-builder__inspector">
                  <div className="visual-builder__title">{t('templateEditor.visualInspector', 'Inspector')}</div>
                  {!selectedVisualBlock ? (
                    <div className="visual-builder__empty">{t('templateEditor.visualSelectBlock', 'Select a block to edit its properties.')}</div>
                  ) : (
                    <div className="visual-builder__fields">
                      <div className="visual-builder__field">
                        {renderInspectorLabel(t('templateEditor.visualType', 'Type'))}
                        <input value={selectedVisualBlock.type} disabled />
                      </div>

                      {selectedVisualBlock.type === 'flexContainer' && (
                        <>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualFlexDirection', 'Direction'))}<select value={selectedVisualBlock.props?.direction || 'row'} onChange={(e) => updateSelectedVisualProps({ direction: e.target.value })}><option value="row">Row</option><option value="column">Column</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualFlexWrap', 'Wrap'))}<select value={selectedVisualBlock.props?.wrap || 'wrap'} onChange={(e) => updateSelectedVisualProps({ wrap: e.target.value })}><option value="nowrap">No wrap</option><option value="wrap">Wrap</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualFlexJustify', 'Justify content'))}<select value={selectedVisualBlock.props?.justify || 'flex-start'} onChange={(e) => updateSelectedVisualProps({ justify: e.target.value })}><option value="flex-start">Start</option><option value="center">Center</option><option value="flex-end">End</option><option value="space-between">Space between</option><option value="space-around">Space around</option><option value="space-evenly">Space evenly</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualFlexAlign', 'Align items'))}<select value={selectedVisualBlock.props?.align || 'stretch'} onChange={(e) => updateSelectedVisualProps({ align: e.target.value })}><option value="stretch">Stretch</option><option value="flex-start">Start</option><option value="center">Center</option><option value="flex-end">End</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualAlignContent', 'Align content'))}<select value={selectedVisualBlock.props?.alignContent || 'stretch'} onChange={(e) => updateSelectedVisualProps({ alignContent: e.target.value })}><option value="stretch">{t('templateEditor.visualOptionStretch', 'Stretch')}</option><option value="flex-start">{t('templateEditor.visualOptionStart', 'Start')}</option><option value="center">{t('templateEditor.visualOptionCenter', 'Center')}</option><option value="flex-end">{t('templateEditor.visualOptionEnd', 'End')}</option><option value="space-between">{t('templateEditor.visualOptionSpaceBetween', 'Space between')}</option><option value="space-around">{t('templateEditor.visualOptionSpaceAround', 'Space around')}</option><option value="space-evenly">{t('templateEditor.visualOptionSpaceEvenly', 'Space evenly')}</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualGap', 'Gap (px)'))}<input type="number" min={0} value={selectedVisualBlock.props?.gap || 0} onChange={(e) => updateSelectedVisualProps({ gap: Number(e.target.value) || 0 })} /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualWidth', 'Width'))}<input value={selectedVisualBlock.props?.width || '100%'} onChange={(e) => updateSelectedVisualProps({ width: e.target.value })} placeholder="100%" /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualHeight', 'Height'))}<input value={selectedVisualBlock.props?.height || ''} onChange={(e) => updateSelectedVisualProps({ height: e.target.value })} placeholder={t('templateEditor.visualHeightPlaceholder', 'auto / 120px')} /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualMinHeight', 'Min height'))}<input value={selectedVisualBlock.props?.minHeight || ''} onChange={(e) => updateSelectedVisualProps({ minHeight: e.target.value })} placeholder="80px" /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualPadding', 'Padding'))}<input value={selectedVisualBlock.props?.padding || ''} onChange={(e) => updateSelectedVisualProps({ padding: e.target.value })} placeholder="12px" /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualMargin', 'Margin'))}<input value={selectedVisualBlock.props?.margin || ''} onChange={(e) => updateSelectedVisualProps({ margin: e.target.value })} placeholder="0 0 12px" /></div>
                          {renderBackgroundField(t('templateEditor.visualBackground', 'Background'))}
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualBorderRadius', 'Border radius'))}<input value={selectedVisualBlock.props?.borderRadius || ''} onChange={(e) => updateSelectedVisualProps({ borderRadius: e.target.value })} placeholder="8px" /></div>
                          <div className="visual-builder__field">
                            {renderInspectorLabel(t('templateEditor.visualAddChildren', 'Add child block'))}
                            <div className="visual-builder__child-actions">
                              {VISUAL_BLOCK_TYPES.map((item) => (
                                <button
                                  key={`child-${item.type}`}
                                  type="button"
                                  className="visual-builder__child-add"
                                  onClick={() => insertVisualBlockIntoContainer(selectedVisualBlock.id, item.type)}
                                >
                                  + {t(item.labelKey, item.labelFallback)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      {selectedVisualBlock.type === 'heading' && (
                        <>
                          {renderBindableTextField(t('templateEditor.visualText', 'Text'), 'text', { multiline: false, placeholder: t('templateEditor.visualSectionTitle', 'Section title') })}
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualLevel', 'Level'))}<select value={selectedVisualBlock.props?.level || 2} onChange={(e) => updateSelectedVisualProps({ level: Number(e.target.value) })}><option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option><option value={4}>H4</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualAlign', 'Align'))}<select value={selectedVisualBlock.props?.align || 'left'} onChange={(e) => updateSelectedVisualProps({ align: e.target.value })}><option value="left">{t('templateEditor.visualOptionLeft', 'Left')}</option><option value="center">{t('templateEditor.visualOptionCenter', 'Center')}</option><option value="right">{t('templateEditor.visualOptionRight', 'Right')}</option></select></div>
                          {renderBackgroundField(t('templateEditor.visualBackground', 'Background'))}
                        </>
                      )}

                      {selectedVisualBlock.type === 'text' && (
                        <>
                          {renderBindableTextField(t('templateEditor.visualText', 'Text'), 'text', { multiline: true, rows: 6, placeholder: t('templateEditor.visualParagraphPlaceholder', 'Write your paragraph here...') })}
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'textSingleLine' && (
                        <>
                          {renderBindableTextField(t('templateEditor.visualText', 'Text'), 'text', { multiline: false, placeholder: t('templateEditor.visualTextBindingPlaceholder', 'Text: {{invoice.number}}') })}
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualAlign', 'Align'))}<select value={selectedVisualBlock.props?.align || 'left'} onChange={(e) => updateSelectedVisualProps({ align: e.target.value })}><option value="left">{t('templateEditor.visualOptionLeft', 'Left')}</option><option value="center">{t('templateEditor.visualOptionCenter', 'Center')}</option><option value="right">{t('templateEditor.visualOptionRight', 'Right')}</option></select></div>
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'conditional' && (
                        <>
                          {renderInlineBindingInput(t('templateEditor.visualCondition', 'Condition'), selectedVisualBlock.props?.condition || '', (next) => updateSelectedVisualProps({ condition: next }), { multiline: false, placeholder: t('templateEditor.visualConditionPlaceholder', 'invoice.number or (eq invoice.status "paid")') })}
                          {renderBindableTextField(t('templateEditor.visualThenText', 'Then text'), 'thenText', { multiline: true, rows: 4, placeholder: t('templateEditor.visualTextBindingPlaceholder', 'Text: {{invoice.number}}') })}
                          {renderBindableTextField(t('templateEditor.visualElseText', 'Else text'), 'elseText', { multiline: true, rows: 3, placeholder: t('templateEditor.visualElseTextPlaceholder', 'No invoice number') })}
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'button' && (
                        <>
                          {renderBindableTextField(t('templateEditor.visualLabel', 'Label'), 'label', { multiline: false, placeholder: t('templateEditor.visualButtonPlaceholder', 'Call to action') })}
                          {renderBindableTextField(t('templateEditor.visualUrl', 'URL'), 'href', { multiline: false, placeholder: 'https://...' })}
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualAlign', 'Align'))}<select value={selectedVisualBlock.props?.align || 'left'} onChange={(e) => updateSelectedVisualProps({ align: e.target.value })}><option value="left">{t('templateEditor.visualOptionLeft', 'Left')}</option><option value="center">{t('templateEditor.visualOptionCenter', 'Center')}</option><option value="right">{t('templateEditor.visualOptionRight', 'Right')}</option></select></div>
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'image' && (
                        <>
                          {renderImageSourceField()}
                          {renderBindableTextField(t('templateEditor.visualAlt', 'Alt'), 'alt', { multiline: false, placeholder: t('templateEditor.visualImageAltPlaceholder', 'Image alt text') })}
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualWidth', 'Width'))}<input value={selectedVisualBlock.props?.width || '100%'} onChange={(e) => updateSelectedVisualProps({ width: e.target.value })} /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualHeight', 'Height'))}<input value={selectedVisualBlock.props?.height || 'auto'} onChange={(e) => updateSelectedVisualProps({ height: e.target.value })} placeholder={t('templateEditor.visualImageHeightPlaceholder', 'auto / 80px')} /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualAlignSelf', 'Align self'))}<select value={selectedVisualBlock.props?.alignSelf || 'auto'} onChange={(e) => updateSelectedVisualProps({ alignSelf: e.target.value })}><option value="auto">{t('templateEditor.visualOptionAuto', 'Auto')}</option><option value="flex-start">{t('templateEditor.visualOptionStart', 'Start')}</option><option value="center">{t('templateEditor.visualOptionCenter', 'Center')}</option><option value="flex-end">{t('templateEditor.visualOptionEnd', 'End')}</option><option value="stretch">{t('templateEditor.visualOptionStretch', 'Stretch')}</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualObjectFit', 'Object fit'))}<select value={selectedVisualBlock.props?.objectFit || 'contain'} onChange={(e) => updateSelectedVisualProps({ objectFit: e.target.value })}><option value="contain">{t('templateEditor.visualOptionContain', 'Contain')}</option><option value="cover">{t('templateEditor.visualOptionCover', 'Cover')}</option><option value="fill">{t('templateEditor.visualOptionFill', 'Fill')}</option><option value="none">{t('templateEditor.visualOptionNone', 'None')}</option><option value="scale-down">{t('templateEditor.visualOptionScaleDown', 'Scale down')}</option></select></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualObjectPosition', 'Object position'))}<input value={selectedVisualBlock.props?.objectPosition || 'center center'} onChange={(e) => updateSelectedVisualProps({ objectPosition: e.target.value })} placeholder="center center" /></div>
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualMargin', 'Margin'))}<input value={selectedVisualBlock.props?.margin || '0'} onChange={(e) => updateSelectedVisualProps({ margin: e.target.value })} placeholder="0 auto" /></div>
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'columns2' && (
                        <>
                          {renderBindableTextField(t('templateEditor.visualLeft', 'Left'), 'left', { multiline: true, rows: 4, placeholder: t('templateEditor.visualLeftPlaceholder', 'Left column content') })}
                          {renderBindableTextField(t('templateEditor.visualRight', 'Right'), 'right', { multiline: true, rows: 4, placeholder: t('templateEditor.visualRightPlaceholder', 'Right column content') })}
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'list' && (
                        <>
                          {renderArraySourceField(t('templateEditor.visualArraySource', 'Array source'))}
                          <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualStyle', 'Style'))}<select value={selectedVisualBlock.props?.ordered ? 'ordered' : 'unordered'} onChange={(e) => updateSelectedVisualProps({ ordered: e.target.value === 'ordered' })}><option value="unordered">{t('templateEditor.visualBullets', 'Bullets (ul)')}</option><option value="ordered">{t('templateEditor.visualNumbered', 'Numbered (ol)')}</option></select></div>
                          {renderBindableTextField(t('templateEditor.visualItemTemplate', 'Item template'), 'itemTemplate', { multiline: true, rows: 4, placeholder: t('templateEditor.visualItemTemplatePlaceholder', 'Date: {{this.date}}') })}
                          {renderBindableTextField(t('templateEditor.visualEmptyText', 'Empty text'), 'emptyText', { multiline: false, placeholder: t('templateEditor.visualNoItems', 'No items') })}
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'table' && (
                        <>
                          {renderArraySourceField(t('templateEditor.visualArraySource', 'Array source'))}
                          {normalizeTableColumns(selectedVisualBlock.props?.columns).map((column, index) => (
                            <div className="visual-builder__field" key={`table-col-${index}`}>
                              {renderInspectorLabel(t('templateEditor.visualColumnN', 'Column {{index}}', { index: index + 1 }))}
                              <input value={column.header || ''} onChange={(e) => updateSelectedTableColumn(index, { header: e.target.value })} placeholder={t('templateEditor.visualHeader', 'Header')} />
                              {renderInlineBindingInput(t('templateEditor.visualCellTemplate', 'Cell template'), column.value || '', (next) => updateSelectedTableColumn(index, { value: next }), { multiline: false, placeholder: '{{this.value}}' })}
                              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button type="button" className="btn-secondary" onClick={() => removeSelectedTableColumn(index)}>{t('templateEditor.visualRemoveColumn', 'Remove column')}</button>
                              </div>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <button type="button" className="btn-secondary" onClick={addSelectedTableColumn}>+ {t('templateEditor.visualAddColumn', 'Add column')}</button>
                          </div>
                          {renderBindableTextField(t('templateEditor.visualEmptyText', 'Empty text'), 'emptyText', { multiline: false, placeholder: t('templateEditor.visualNoRows', 'No rows') })}
                          {renderBackgroundField('Background')}
                        </>
                      )}

                      {selectedVisualBlock.type === 'spacer' && (
                        <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualHeightPx', 'Height (px)'))}<input type="number" min={0} value={selectedVisualBlock.props?.height || 0} onChange={(e) => updateSelectedVisualProps({ height: Number(e.target.value) || 0 })} /></div>
                      )}

                      {selectedVisualBlock.type === 'raw' && (
                        <div className="visual-builder__field">{renderInspectorLabel(t('templateEditor.visualHtml', 'HTML'))}<textarea rows={10} value={selectedVisualBlock.props?.html || ''} onChange={(e) => updateSelectedVisualProps({ html: e.target.value })} /></div>
                      )}

                      {renderQuickCssControls()}

                      <div className="visual-builder__field">
                        {renderInspectorLabel(t('templateEditor.visualCustomCss', 'Custom CSS (inline)'))}
                        <textarea
                          rows={4}
                          value={selectedVisualBlock.props?.css || ''}
                          onChange={(e) => updateSelectedVisualProps({ css: e.target.value })}
                          placeholder={t('templateEditor.visualCustomCssPlaceholder', 'margin-top:8px;border:1px solid #ddd;')}
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" className="btn-danger" onClick={removeSelectedVisualBlock}><Trash2 size={14} style={{ marginRight: 4 }} /> {t('templateEditor.visualDeleteBlock', 'Delete block')}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'html' && (
              <Editor
                height="100%"
                language="handlebars"
                theme="vs-dark"
                value={contentHtml}
                onChange={(v) => setContentHtml(v || '')}
                onMount={(editor) => {
                  htmlEditorRef.current = editor;
                }}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                }}
              />
            )}
            {activeTab === 'helpers' && (
              <Editor
                height="100%"
                language="javascript"
                theme="vs-dark"
                value={helpersJs}
                onChange={(v) => setHelpersJs(v || '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                }}
              />
            )}
            {activeTab === 'data' && (
              <Editor
                height="100%"
                language="json"
                theme="vs-dark"
                value={dataJson}
                onChange={(v) => setDataJson(v || '{}')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                }}
              />
            )}
          </div>
        </div>

        {/* Right: Preview */}
        <div className="preview-pane">
          <div className="pane-header">
            <span>{t('templateEditor.preview', 'Preview')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'var(--text-secondary)',
                  fontSize: 11,
                }}
              >
                <span>{t('templateEditor.paperSize', 'Paper')}</span>
                <select
                  value={previewPaperSize}
                  onChange={(e) => setPreviewPaperSize(e.target.value)}
                  style={{ width: 88, padding: '3px 6px', fontSize: 11 }}
                >
                  <option value="A4">A4</option>
                  <option value="LETTER">Letter</option>
                </select>
              </label>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'var(--text-secondary)',
                  fontSize: 11,
                  textTransform: 'none',
                }}
                title={t('templateEditor.debugConsoleHint', 'Capture console.log/warn/error from beforeRender scripts and Helpers JS during preview.')}
              >
                <span>{t('templateEditor.debugConsole', 'Debug console')}</span>
                <span className="switch" style={{ transform: 'scale(0.82)' }}>
                  <input
                    type="checkbox"
                    checked={debugConsoleEnabled}
                    onChange={(e) => setDebugConsoleEnabled(e.target.checked)}
                    aria-label={t('templateEditor.debugConsole', 'Debug console')}
                  />
                  <span className="slider" />
                </span>
              </label>
              <button className="btn-secondary" onClick={handlePreview} style={{ padding: '4px 8px', fontSize: '11px' }} disabled={previewing || pdfRendering}>
                {previewing ? t('templateEditor.previewRefreshing', 'Refreshing...') : t('templateEditor.previewRefresh', 'Refresh')}
              </button>
            </div>
          </div>
          <div className="pane-content preview-content">
            <iframe
              className="preview-iframe"
              title={t('templateEditor.preview', 'Preview')}
              sandbox="allow-scripts"
              srcDoc={wrapPreviewDoc(
                previewHtml || `<div style="font-family:sans-serif;padding:12px;color:#9ca3af;">${t('templateEditor.previewHint', 'Click Preview to generate the preview.')}</div>`,
                { paperSize: previewPaperSize },
              )}
            />
          </div>
          <div className={`preview-console ${debugConsoleCollapsed ? 'is-collapsed' : ''}`}>
            <div className="preview-console__header">
              <span>{t('templateEditor.consoleTitle', 'Console')}</span>
              <div className="preview-console__actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPreviewConsoleLogs([])}
                  style={{ padding: '3px 8px', fontSize: '11px' }}
                  disabled={previewConsoleLogs.length === 0}
                >
                  {t('templateEditor.consoleClear', 'Clear')}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setDebugConsoleCollapsed((v) => !v)}
                  style={{ padding: '3px 6px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  title={debugConsoleCollapsed
                    ? t('templateEditor.consoleExpand', 'Expand console')
                    : t('templateEditor.consoleCollapse', 'Collapse console')}
                  aria-label={debugConsoleCollapsed
                    ? t('templateEditor.consoleExpand', 'Expand console')
                    : t('templateEditor.consoleCollapse', 'Collapse console')}
                >
                  {debugConsoleCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </div>
            </div>
            <div className="preview-console__body">
              {previewConsoleLogs.length === 0 ? (
                <div className="preview-console__empty">
                  {debugConsoleEnabled
                    ? t('templateEditor.consoleEmpty', 'No console output yet. Run Preview to capture helper/script logs.')
                    : t('templateEditor.consoleDisabled', 'Debug console is disabled.')}
                </div>
              ) : (
                previewConsoleLogs.map((entry, idx) => (
                  <div key={`${entry.ts || 'ts'}-${idx}`} className={`preview-console__line preview-console__line--${entry.level || 'info'}`}>
                    <span className="preview-console__meta">[{entry.level || 'info'}{entry.source ? ` · ${entry.source}` : ''}]</span>
                    <span>{entry.msg || ''}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Generation Dialog */}
      {aiDialogOpen && (
        <div className="modal-overlay" onClick={() => !aiGenerating && setAiDialogOpen(false)}>
          <div className="modal app-alert" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="app-alert__head">
              <Sparkles size={20} style={{ color: 'var(--accent)' }} />
              <h3 className="app-alert__title">{t('templateEditor.aiPromptTitle', 'Generate Template with AI')}</h3>
            </div>
            <textarea
              rows={5}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value.slice(0, AI_PROMPT_MAX_CHARS))}
              placeholder={t('templateEditor.aiPromptPlaceholder', 'Describe the report template you need...')}
              style={{ width: '100%', marginBottom: 14, fontFamily: 'inherit', resize: 'vertical' }}
              disabled={aiGenerating}
              maxLength={AI_PROMPT_MAX_CHARS}
              autoFocus
            />
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', marginBottom: 6, color: 'var(--text-secondary)', fontSize: 12 }}>
                {t('templateEditor.aiGenerationModeLabel', 'Generate')}
              </label>
              <select
                value={aiGenerationMode}
                onChange={(e) => setAiGenerationMode(e.target.value)}
                disabled={aiGenerating}
              >
                <option value="all">{t('templateEditor.aiGenerationModeAll', 'HTML + Helpers + Sample Data')}</option>
                <option value="html">{t('templateEditor.aiGenerationModeHtml', 'Only HTML')}</option>
                <option value="helpers">{t('templateEditor.aiGenerationModeHelpers', 'Only Helpers JS')}</option>
                <option value="sampleData">{t('templateEditor.aiGenerationModeSampleData', 'Only Sample Data')}</option>
              </select>
            </div>
            <div style={{ marginBottom: 14, color: 'var(--text-muted)', fontSize: 12, textAlign: 'right' }}>
              {t('templateEditor.aiPromptCounter', '{{count}} / {{max}} characters', {
                count: aiPrompt.length,
                max: AI_PROMPT_MAX_CHARS,
              })}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setAiDialogOpen(false)} disabled={aiGenerating}>
                {t('billing.cancel', 'Cancel')}
              </button>
              <button className="btn-primary" onClick={handleAIGenerate} disabled={aiGenerating || !aiPrompt.trim()}>
                {aiGenerating ? (
                  <><span className="inline-spinner" /> {t('templateEditor.aiGenerating', 'Generating...')}</>
                ) : (
                  <><Sparkles size={14} style={{ marginRight: 4 }} /> {t('templateEditor.aiGenerate', 'Generate with AI')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Versions Dialog */}
      {versionsOpen && (
        <div className="modal-overlay" onClick={() => !versionsSaving && setVersionsOpen(false)}>
          <div className="modal app-alert" style={{ maxWidth: 720, width: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="app-alert__head">
              <History size={20} style={{ color: 'var(--accent)' }} />
              <h3 className="app-alert__title">{t('templateEditor.versionsTitle', 'Template Versions')}</h3>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                {t('templateEditor.versionHistory', 'Version history')}
              </div>

              {versionsLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('templateEditor.loadingVersions', 'Loading versions...')}</div>
              ) : versions.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('templateEditor.noVersions', 'No versions yet.')}</div>
              ) : (
                <div style={{ maxHeight: 300, overflow: 'auto', display: 'grid', gap: 8 }}>
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        background: activeVersionId === v.id ? 'var(--bg-secondary)' : 'transparent',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong>{v.label || `v${v.version_number}`}</strong>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>#{v.version_number}</span>
                          {v.is_published ? (
                            <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Check size={12} /> {t('templateEditor.published', 'Published')}
                            </span>
                          ) : null}
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {new Date(v.created_at).toLocaleString()}
                        </span>
                      </div>

                      {v.change_note ? (
                        <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-secondary)' }}>{v.change_note}</div>
                      ) : null}

                      <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button className="btn-secondary" onClick={() => handleRestoreVersion(v.id)} disabled={versionsSaving}>
                          <RotateCcw size={12} style={{ marginRight: 4 }} /> {t('templateEditor.versionRollback', 'Rollback')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: 14 }}>
              <button className="btn-secondary" onClick={() => setVersionsOpen(false)} disabled={versionsSaving}>
                {t('billing.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
